// ============================================================
//  src/pages/HowToUsePage.js
//  User-facing developer guide shown from the sidebar
//  Fully responsive — optimized for mobile & desktop
// ============================================================
import React, { useState, useEffect, useRef } from 'react';
import { DOCS_MARKDOWN } from './docsMarkdown';

// Triggers a browser download of the full developer guide as a .md file.
// DOCS_MARKDOWN is a pre-generated snapshot of this page's content (see
// docsMarkdown.js) -- it is NOT auto-synced with edits made here. If you
// change section content in this file, regenerate docsMarkdown.js to match
// (ask the team for the extraction script used to build it originally).
function downloadMarkdown() {
  const blob = new Blob([DOCS_MARKDOWN], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'commandkit-how-to-use.md';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Sidebar nav items ─────────────────────────────────────────
// Grouped into categories so the sidebar reads as a map of the product,
// not a flat list of 18 undifferentiated links. NAV_GROUPS is the single
// source of truth; SECTIONS is derived from it for scroll-spy logic.
const NAV_GROUPS = [
  {
    category: 'Getting Started',
    items: [
      { id: 'overview',   icon: '🗺️', label: 'Overview' },
      { id: 'setup',      icon: '⚙️', label: 'Account Setup' },
      { id: 'create-bot', icon: '🤖', label: 'Create a Bot' },
    ],
  },
  {
    category: 'Bot Health & Profile',
    items: [
      { id: 'health',  icon: '💚', label: 'WhatsApp Health' },
      { id: 'profile', icon: '🪪', label: 'Business Profile' },
    ],
  },
  {
    category: 'Building Your Bot',
    items: [
      { id: 'commands', icon: '⌨️', label: 'Commands' },
      { id: 'panel',    icon: '🗂️', label: 'Panel / Config' },
      { id: 'storage',  icon: '🗄️', label: 'Bot Storage' },
    ],
  },
  {
    category: 'WhatsApp Flows',
    items: [
      { id: 'flows',         icon: '🌊', label: 'WhatsApp Flows' },
      { id: 'flow-endpoint', icon: '🔗', label: 'Flow Endpoint' },
    ],
  },
  {
    category: 'Integrations & Automation',
    items: [
      { id: 'endpoints',     icon: '🧩', label: 'Custom Endpoints' },
      { id: 'broadcasts',    icon: '📣', label: 'Broadcasts' },
      { id: 'scheduled',     icon: '⏰', label: 'Scheduled Messages' },
      { id: 'import-export', icon: '📦', label: 'Import & Export' },
    ],
  },
  {
    category: 'Operations',
    items: [
      { id: 'users-logs', icon: '📊', label: 'Users & Logs' },
      { id: 'inbox',      icon: '💬', label: 'Agent Inbox' },
    ],
  },
  {
    category: 'API Reference',
    items: [
      { id: 'send-api', icon: '📨', label: 'Send API' },
    ],
  },
  {
    category: 'Troubleshooting',
    items: [
      { id: 'errors', icon: '🛠️', label: 'Common Errors' },
    ],
  },
];

const SECTIONS = NAV_GROUPS.flatMap(g => g.items);

// ── Reusable components ───────────────────────────────────────
function SectionHeading({ id, icon, title, subtitle }) {
  return (
    <div id={id} style={{ marginBottom: 'clamp(20px, 5vw, 32px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'clamp(22px, 6vw, 26px)' }}>{icon}</span>
        <h2 style={{ 
          margin: 0, 
          fontSize: 'clamp(18px, 5vw, 22px)', 
          fontWeight: 800, 
          color: 'var(--text-dark)' 
        }}>{title}</h2>
      </div>
      {subtitle && (
        <p style={{ 
          margin: '8px 0 0 0', 
          fontSize: 'clamp(12px, 3.5vw, 14px)', 
          color: 'var(--text-muted)', 
          lineHeight: 1.6 
        }}>{subtitle}</p>
      )}
      <div style={{ height: 1, background: 'var(--border)', marginTop: 'clamp(12px, 4vw, 16px)' }} />
    </div>
  );
}

function Step({ number, title, children }) {
  return (
    <div style={{ display: 'flex', gap: 'clamp(12px, 4vw, 16px)', marginBottom: 'clamp(20px, 5vw, 24px)' }}>
      <div style={{
        flexShrink: 0, 
        width: 'clamp(28px, 7vw, 32px)', 
        height: 'clamp(28px, 7vw, 32px)', 
        borderRadius: '50%',
        background: 'var(--primary)', 
        color: '#fff',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        fontSize: 'clamp(12px, 3.5vw, 13px)', 
        fontWeight: 800, 
        marginTop: 2,
      }}>{number}</div>
      <div style={{ flex: 1 }}>
        <div style={{ 
          fontWeight: 700, 
          fontSize: 'clamp(14px, 4vw, 15px)', 
          color: 'var(--text-dark)', 
          marginBottom: 6 
        }}>{title}</div>
        <div style={{ 
          fontSize: 'clamp(12px, 3.5vw, 13.5px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7 
        }}>{children}</div>
      </div>
    </div>
  );
}

function InfoBox({ type = 'info', children }) {
  const styles = {
    info:    { bg: 'rgba(0,44,237,0.06)',  border: 'rgba(0,44,237,0.2)',  icon: 'ℹ️' },
    warning: { bg: 'rgba(255,149,0,0.08)', border: 'rgba(255,149,0,0.3)', icon: '⚠️' },
    success: { bg: 'rgba(0,196,140,0.07)', border: 'rgba(0,196,140,0.3)', icon: '✅' },
    tip:     { bg: 'rgba(0,122,255,0.06)', border: 'rgba(0,122,255,0.2)', icon: '💡' },
  };
  const s = styles[type] ?? styles.info;
  return (
    <div style={{
      background: s.bg, 
      border: `1px solid ${s.border}`,
      borderRadius: 'clamp(8px, 3vw, 10px)', 
      padding: 'clamp(10px, 4vw, 12px) clamp(12px, 4vw, 16px)', 
      marginBottom: 16,
      display: 'flex', 
      gap: 'clamp(8px, 3vw, 10px)', 
      fontSize: 'clamp(12px, 3.5vw, 13.5px)', 
      lineHeight: 1.6,
      color: 'var(--text-mid)',
    }}>
      <span style={{ flexShrink: 0, marginTop: 1, fontSize: 'clamp(14px, 4vw, 16px)' }}>{s.icon}</span>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function CodeBlock({ children, label }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(children).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }
  return (
    <div style={{ marginBottom: 16 }}>
      {label && (
        <div style={{ 
          fontSize: 'clamp(10px, 3vw, 11px)', 
          fontWeight: 700, 
          color: 'var(--text-muted)', 
          textTransform: 'uppercase', 
          letterSpacing: '0.5px', 
          marginBottom: 6 
        }}>
          {label}
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <pre style={{
          margin: 0, 
          padding: 'clamp(12px, 4vw, 14px) clamp(12px, 4vw, 16px)', 
          paddingRight: 'clamp(50px, 15vw, 60px)',
          background: '#0D0F1A', 
          color: '#e2e8f0',
          borderRadius: 'clamp(8px, 3vw, 10px)', 
          fontSize: 'clamp(11px, 3.2vw, 12.5px)', 
          fontFamily: 'monospace',
          lineHeight: 1.7, 
          overflowX: 'auto', 
          whiteSpace: 'pre-wrap', 
          wordBreak: 'break-all',
          WebkitOverflowScrolling: 'touch',
        }}>
          {children}
        </pre>
        <button
          onClick={copy}
          style={{
            position: 'absolute', 
            top: 10, 
            right: 10,
            background: copied ? 'var(--success)' : 'rgba(255,255,255,0.1)',
            border: 'none', 
            borderRadius: 6, 
            color: '#fff',
            fontSize: 'clamp(10px, 3vw, 11px)', 
            fontWeight: 700, 
            padding: '4px 10px',
            cursor: 'pointer', 
            transition: 'background 0.2s',
            zIndex: 1,
          }}>
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function Table({ headers, rows }) {
  return (
    <div style={{ 
      overflowX: 'auto', 
      marginBottom: 20,
      WebkitOverflowScrolling: 'touch',
      borderRadius: 'clamp(8px, 3vw, 10px)',
      border: '1px solid var(--border)',
    }}>
      <table style={{ 
        width: '100%', 
        borderCollapse: 'collapse', 
        fontSize: 'clamp(12px, 3.5vw, 13.5px)',
        minWidth: 280,
      }}>
        <thead>
          <tr>
            {headers.map(h => (
              <th key={h} style={{
                textAlign: 'left', 
                padding: 'clamp(8px, 3vw, 10px) clamp(10px, 3vw, 14px)',
                background: 'var(--surface)', 
                color: 'var(--text-mid)',
                fontWeight: 700, 
                fontSize: 'clamp(11px, 3.2vw, 12px)', 
                textTransform: 'uppercase',
                letterSpacing: '0.4px', 
                borderBottom: '2px solid var(--border)',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
              {row.map((cell, j) => (
                <td key={j} style={{ 
                  padding: 'clamp(8px, 3vw, 10px) clamp(10px, 3vw, 14px)', 
                  color: 'var(--text-mid)', 
                  verticalAlign: 'top', 
                  lineHeight: 1.6,
                  wordBreak: 'break-word',
                }}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Tag({ children, color = 'var(--primary)' }) {
  return (
    <span style={{
      display: 'inline-block', 
      background: color + '18',
      color, 
      fontSize: 'clamp(10px, 3vw, 11px)', 
      fontWeight: 700, 
      padding: '2px 8px',
      borderRadius: 20, 
      fontFamily: 'monospace',
      margin: '2px 4px 2px 0',
    }}>
      {children}
    </span>
  );
}

function Card({ icon, title, children }) {
  return (
    <div style={{
      background: 'var(--bg-card)', 
      border: '1px solid var(--border)',
      borderRadius: 'clamp(10px, 4vw, 12px)', 
      padding: 'clamp(14px, 4vw, 18px) clamp(16px, 4vw, 20px)', 
      marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span style={{ fontSize: 'clamp(18px, 5vw, 20px)' }}>{icon}</span>
        <span style={{ 
          fontWeight: 700, 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          color: 'var(--text-dark)' 
        }}>{title}</span>
      </div>
      <div style={{ 
        fontSize: 'clamp(12px, 3.5vw, 13.5px)', 
        color: 'var(--text-mid)', 
        lineHeight: 1.7 
      }}>{children}</div>
    </div>
  );
}

// ── Shared grouped nav list (used by both desktop sidebar & mobile sheet) ──
function NavGroups({ activeSection, onSelect, collapsed, onToggleCategory, itemPadding }) {
  return (
    <>
      {NAV_GROUPS.map(group => {
        const isCollapsed = !!collapsed[group.category];
        const groupHasActive = group.items.some(i => i.id === activeSection);
        return (
          <div key={group.category} style={{ marginBottom: 2 }}>
            <button
              onClick={() => onToggleCategory(group.category)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: `10px ${itemPadding}px 6px`,
                fontSize: 11,
                fontWeight: 800,
                color: groupHasActive ? 'var(--primary)' : 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
              }}
            >
              <span>{group.category}</span>
              <span style={{
                fontSize: 10,
                transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.15s',
                color: 'var(--text-muted)',
              }}>▾</span>
            </button>
            {!isCollapsed && group.items.map(s => (
              <button
                key={s.id}
                onClick={() => onSelect(s.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: `9px ${itemPadding}px`,
                  border: 'none',
                  background: activeSection === s.id ? 'var(--primary-light)' : 'transparent',
                  color: activeSection === s.id ? 'var(--primary)' : 'var(--text-mid)',
                  fontWeight: activeSection === s.id ? 700 : 500,
                  fontSize: 13.5,
                  cursor: 'pointer',
                  textAlign: 'left',
                  borderLeft: activeSection === s.id ? '3px solid var(--primary)' : '3px solid transparent',
                  transition: 'all 0.15s',
                }}>
                <span style={{ fontSize: 15 }}>{s.icon}</span>
                {s.label}
              </button>
            ))}
          </div>
        );
      })}
    </>
  );
}

// ── Send API payload reference (tabbed by message type) ─────────
// This exists because the #1 support question is "what do I actually put
// in the request body?" — a prose description isn't enough for `template`
// and `interactive`, which have several real sub-shapes. Every tab below
// shows a complete, copy-pasteable JSON body plus the exact response shape.
const SEND_API_TYPES = [
  {
    id: 'text',
    label: 'text',
    fields: [['to', 'string', 'yes', "Recipient's number, digits only, with country code"], ['type', 'string', 'yes', '"text"'], ['message', 'string', 'yes', 'The text to send']],
    body: `{
  "to": "2348012345678",
  "type": "text",
  "message": "Hello from the Send API!"
}`,
    response: `{
  "success": true,
  "message": "Message sent.",
  "wa_message_id": "wamid.HBgLMjM0ODAxMjM0NTY3OBUCABEYEjBB..."
}`,
  },
  {
    id: 'image',
    label: 'image',
    fields: [['to', 'string', 'yes', 'Recipient number'], ['type', 'string', 'yes', '"image"'], ['url', 'string', 'yes', 'Public HTTPS URL of the image'], ['caption', 'string', 'no', 'Optional caption shown under the image']],
    body: `{
  "to": "2348012345678",
  "type": "image",
  "url": "https://example.com/product-photo.jpg",
  "caption": "Here's the item you asked about"
}`,
    response: `{ "success": true, "message": "Message sent.", "wa_message_id": "wamid.HBg..." }`,
  },
  {
    id: 'document',
    label: 'document',
    fields: [['to', 'string', 'yes', 'Recipient number'], ['type', 'string', 'yes', '"document"'], ['url', 'string', 'yes', 'Public HTTPS URL of the file'], ['filename', 'string', 'no', 'Filename shown to the recipient'], ['caption', 'string', 'no', 'Optional caption']],
    body: `{
  "to": "2348012345678",
  "type": "document",
  "url": "https://example.com/invoice-1042.pdf",
  "filename": "Invoice-1042.pdf",
  "caption": "Your invoice is attached"
}`,
    response: `{ "success": true, "message": "Message sent.", "wa_message_id": "wamid.HBg..." }`,
  },
  {
    id: 'interactive-buttons',
    label: 'interactive · buttons',
    group: 'interactive',
    fields: [['to', 'string', 'yes', 'Recipient number'], ['type', 'string', 'yes', '"interactive"'], ['interactive', 'object', 'yes', "Meta's interactive object, unchanged — see below"]],
    note: 'Up to 3 reply buttons. This is the raw WhatsApp Cloud API shape — the platform does not reshape it for you, so it must match Meta\'s schema exactly.',
    body: `{
  "to": "2348012345678",
  "type": "interactive",
  "interactive": {
    "type": "button",
    "body": { "text": "How was your delivery?" },
    "action": {
      "buttons": [
        { "type": "reply", "reply": { "id": "good",    "title": "👍 Good" } },
        { "type": "reply", "reply": { "id": "bad",     "title": "👎 Bad"  } },
        { "type": "reply", "reply": { "id": "support", "title": "Talk to support" } }
      ]
    }
  }
}`,
    response: `{ "success": true, "message": "Message sent.", "wa_message_id": "wamid.HBg..." }`,
  },
  {
    id: 'interactive-list',
    label: 'interactive · list',
    group: 'interactive',
    fields: [['to', 'string', 'yes', 'Recipient number'], ['type', 'string', 'yes', '"interactive"'], ['interactive', 'object', 'yes', "Meta's interactive object"]],
    note: 'A tappable menu grouped into sections. "id" is what comes back in the reply webhook — use it to know which row was picked.',
    body: `{
  "to": "2348012345678",
  "type": "interactive",
  "interactive": {
    "type": "list",
    "body": { "text": "What would you like to order?" },
    "action": {
      "button": "View menu",
      "sections": [
        {
          "title": "Drinks",
          "rows": [
            { "id": "drink_coffee", "title": "Coffee", "description": "Hot, ₦1,500" },
            { "id": "drink_tea",    "title": "Tea",    "description": "Hot, ₦1,000" }
          ]
        },
        {
          "title": "Snacks",
          "rows": [
            { "id": "snack_pie", "title": "Meat pie", "description": "₦800" }
          ]
        }
      ]
    }
  }
}`,
    response: `{ "success": true, "message": "Message sent.", "wa_message_id": "wamid.HBg..." }`,
  },
  {
    id: 'interactive-cta',
    label: 'interactive · cta_url',
    group: 'interactive',
    fields: [['to', 'string', 'yes', 'Recipient number'], ['type', 'string', 'yes', '"interactive"'], ['interactive', 'object', 'yes', "Meta's interactive object"]],
    note: 'Exactly one button that opens a link. Can\'t be combined with reply buttons in the same message.',
    body: `{
  "to": "2348012345678",
  "type": "interactive",
  "interactive": {
    "type": "cta_url",
    "body": { "text": "Your order has shipped!" },
    "action": {
      "name": "cta_url",
      "parameters": {
        "display_text": "Track order",
        "url": "https://example.com/track/1042"
      }
    }
  }
}`,
    response: `{ "success": true, "message": "Message sent.", "wa_message_id": "wamid.HBg..." }`,
  },
  {
    id: 'interactive-flow',
    label: 'interactive · flow',
    group: 'interactive',
    fields: [['to', 'string', 'yes', 'Recipient number'], ['type', 'string', 'yes', '"interactive"'], ['interactive', 'object', 'yes', "Meta's interactive object"]],
    note: <>Opens one of your bot's WhatsApp Flows. <code>flow_id</code> is Meta's numeric Flow ID (Flows tab → your flow → Meta Flow ID), not this platform's internal flow name.</>,
    body: `{
  "to": "2348012345678",
  "type": "interactive",
  "interactive": {
    "type": "flow",
    "body": { "text": "Let's get you signed up" },
    "action": {
      "name": "flow",
      "parameters": {
        "flow_message_version": "3",
        "flow_token": "unique-per-send-token",
        "flow_id": "1234567890123456",
        "flow_cta": "Start",
        "flow_action": "navigate"
      }
    }
  }
}`,
    response: `{ "success": true, "message": "Message sent.", "wa_message_id": "wamid.HBg..." }`,
  },
  {
    id: 'template',
    label: 'template',
    fields: [['to', 'string', 'yes', 'Recipient number'], ['type', 'string', 'yes', '"template"'], ['name', 'string', 'yes', 'Approved template name (from the Templates tab)'], ['lang', 'string', 'no', 'Language code, defaults to "en_US"'], ['components', 'array', 'no', "Fills in {{1}}, {{2}}... variables and buttons — omit only if the template has none"]],
    note: 'The template must already be APPROVED in the Templates tab. "components" follows Meta\'s standard template-parameter format.',
    body: `{
  "to": "2348012345678",
  "type": "template",
  "name": "order_shipped",
  "lang": "en_US",
  "components": [
    {
      "type": "body",
      "parameters": [
        { "type": "text", "text": "John" },
        { "type": "text", "text": "1042" }
      ]
    }
  ]
}`,
    response: `{ "success": true, "message": "Message sent.", "wa_message_id": "wamid.HBg..." }`,
  },
  {
    id: 'template-otp',
    label: 'template · OTP',
    group: 'template',
    fields: [['to', 'string', 'yes', 'Recipient number'], ['type', 'string', 'yes', '"template"'], ['name', 'string', 'yes', 'Your AUTHENTICATION-category template name'], ['components', 'array', 'yes', 'One body parameter with the code, one button parameter that repeats it']],
    note: (
      <>
        OTP / "copy code" is <strong>not</strong> a separate <code>type</code> — it's an AUTHENTICATION template
        (built in the Templates tab with an <code>OTP</code> / <code>COPY_CODE</code> button) sent the normal{' '}
        <code>type: "template"</code> way. The code itself is passed twice: once as the body text parameter, once
        as the button's <code>coupon_code</code> parameter.
      </>
    ),
    body: `{
  "to": "2348012345678",
  "type": "template",
  "name": "login_otp",
  "lang": "en_US",
  "components": [
    { "type": "body", "parameters": [{ "type": "text", "text": "482910" }] },
    {
      "type": "button",
      "sub_type": "copy_code",
      "index": "0",
      "parameters": [{ "type": "coupon_code", "coupon_code": "482910" }]
    }
  ]
}`,
    response: `{ "success": true, "message": "Message sent.", "wa_message_id": "wamid.HBg..." }`,
  },
  {
    id: 'raw',
    label: 'raw',
    fields: [['to', 'string', 'yes', 'Recipient number (also auto-filled into payload.to if omitted there)'], ['type', 'string', 'yes', '"raw"'], ['payload', 'object', 'yes', "The complete WhatsApp Cloud API message payload"]],
    note: "Escape hatch for anything not covered above — location messages, contacts, reactions, or a shape Meta adds later. messaging_product defaults to \"whatsapp\" if you leave it out.",
    body: `{
  "to": "2348012345678",
  "type": "raw",
  "payload": {
    "messaging_product": "whatsapp",
    "to": "2348012345678",
    "type": "location",
    "location": {
      "latitude": "6.5244",
      "longitude": "3.3792",
      "name": "Our Store"
    }
  }
}`,
    response: `{ "success": true, "message": "Message sent.", "wa_message_id": "wamid.HBg..." }`,
  },
];

function SendApiExplorer() {
  const [activeId, setActiveId] = useState('text');
  const active = SEND_API_TYPES.find(t => t.id === activeId) || SEND_API_TYPES[0];

  const curl = `curl -X POST https://www.cmdkit.im/api/send/ \\
  -H "Content-Type: application/json" \\
  -H "X-Send-Key: sk_your_key_here" \\
  -d '${active.body}'`;

  const js = `const res = await fetch('https://www.cmdkit.im/api/send/', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-Send-Key': process.env.WABOT_SEND_KEY,
  },
  body: JSON.stringify(${active.body.replace(/^/gm, '  ').trim()}),
});
const data = await res.json();`;

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Tabs */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        marginBottom: 16,
        borderBottom: '1px solid var(--border)',
        paddingBottom: 12,
      }}>
        {SEND_API_TYPES.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveId(t.id)}
            style={{
              padding: '6px 12px',
              borderRadius: 20,
              border: activeId === t.id ? '1px solid var(--primary)' : '1px solid var(--border)',
              background: activeId === t.id ? 'var(--primary)' : 'var(--bg-card)',
              color: activeId === t.id ? '#fff' : 'var(--text-mid)',
              fontSize: 'clamp(11px, 3vw, 12px)',
              fontWeight: 700,
              fontFamily: 'monospace',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {active.note && <InfoBox type="info">{active.note}</InfoBox>}

      <Table
        headers={['Field', 'Type', 'Required', 'Notes']}
        rows={active.fields.map(([f, ty, req, notes]) => [
          <code key={f}>{f}</code>,
          ty,
          req === 'yes' ? <strong style={{ color: 'var(--success)' }}>required</strong> : 'optional',
          notes,
        ])}
      />

      <CodeBlock label={`Request body — type="${active.id.startsWith('interactive') ? 'interactive' : active.id.startsWith('template') ? 'template' : active.id}"`}>
        {active.body}
      </CodeBlock>

      <CodeBlock label="Full cURL example (paste directly into a terminal)">
        {curl}
      </CodeBlock>

      <CodeBlock label="Full JavaScript example (fetch)">
        {js}
      </CodeBlock>

      <CodeBlock label="Success response">
        {active.response}
      </CodeBlock>
    </div>
  );
}

// ── Mobile Bottom Sheet Navigation ─────────────────────────────
function MobileNav({ activeSection, onSelect, isOpen, onClose, collapsed, onToggleCategory }) {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          animation: 'fadeIn 0.2s ease',
        }}
      />
      
      {/* Bottom Sheet */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'var(--bg-card)',
          borderRadius: '20px 20px 0 0',
          zIndex: 1001,
          maxHeight: '70vh',
          overflowY: 'auto',
          animation: 'slideUp 0.3s ease',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          background: 'var(--bg-card)',
          zIndex: 2,
        }}>
          <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-dark)' }}>
            📖 Guide Sections
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 20,
              cursor: 'pointer',
              color: 'var(--text-mid)',
              padding: 8,
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: '8px 0 20px' }}>
          <NavGroups
            activeSection={activeSection}
            onSelect={(id) => { onSelect(id); onClose(); }}
            collapsed={collapsed}
            onToggleCategory={onToggleCategory}
            itemPadding={20}
          />
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function HowToUsePage() {
  const [activeSection, setActiveSection] = useState('overview');
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // Category collapse state for the grouped sidebar. All expanded by default
  // so nothing is hidden on first visit; users can collapse groups they
  // don't need to shorten the list.
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const contentRef = useRef(null);

  // The desktop sidebar used to be `position: sticky`, but this app has a
  // documented history of layout rules silently failing somewhere in the
  // ancestor chain (see the comment in AppLayout.js about the Inbox route),
  // and that's exactly what was happening here too — sticky was resolving
  // against an unintended ancestor and never actually engaging, so the nav
  // just scrolled away with the page. `position: fixed` sidesteps that
  // whole class of bug: it's anchored to the viewport no matter what any
  // ancestor's overflow/height does. The only thing fixed positioning needs
  // that sticky doesn't is its `left` offset, since it's taken out of the
  // normal flex flow — we measure that once from an invisible spacer that
  // stays in flow, so the nav lines up correctly regardless of the app's
  // own sidebar width or the page's centered max-width column.
  const asideSpacerRef = useRef(null);
  const [asideLeft, setAsideLeft] = useState(null);

  useEffect(() => {
    if (isMobile) return;
    function measure() {
      if (asideSpacerRef.current) {
        setAsideLeft(asideSpacerRef.current.getBoundingClientRect().left);
      }
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isMobile]);

  function toggleCategory(category) {
    setCollapsedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  }

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  function scrollTo(id) {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) {
      const offset = isMobile ? 80 : 20;
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  }

  // Update active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = SECTIONS.map(s => document.getElementById(s.id)).filter(Boolean);
      const scrollPosition = window.scrollY + (isMobile ? 100 : 120);
      
      let current = sections[0]?.id || 'overview';
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.offsetTop <= scrollPosition) {
          current = section.id;
          break;
        }
      }
      setActiveSection(current);
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Grid for cards - responsive
  const cardGridStyle = isMobile
    ? { display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 32 }
    : { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 32 };

  return (
    <div style={{ 
      display: 'flex', 
      minHeight: '100vh', 
      background: 'var(--page-bg)',
      position: 'relative',
    }}>

      {/* ── Desktop Left Nav (hidden on mobile) ──────────────────────
          A plain, static, non-positioned spacer that reserves the 220px
          column in the flex row (so <main> doesn't shift), plus a
          `position: fixed` panel — measured against that spacer — that
          holds the actual nav. See the comment above asideSpacerRef for
          why this replaced `position: sticky`. */}
      {!isMobile && (
        <>
          <aside ref={asideSpacerRef} style={{ width: 220, flexShrink: 0 }} aria-hidden="true" />
          <aside style={{
            width: 220,
            position: 'fixed',
            top: 0,
            left: asideLeft ?? 0,
            height: '100vh',
            overflowY: 'auto',
            padding: '28px 0',
            borderRight: '1px solid var(--border)',
            background: 'var(--bg-card)',
            visibility: asideLeft === null ? 'hidden' : 'visible',
            zIndex: 20,
          }}>
            <div style={{ 
              padding: '0 20px 16px', 
              fontSize: 11, 
              fontWeight: 800, 
              color: 'var(--text-muted)', 
              textTransform: 'uppercase', 
              letterSpacing: '0.6px' 
            }}>
              How to Use
            </div>
            <NavGroups
              activeSection={activeSection}
              onSelect={scrollTo}
              collapsed={collapsedCategories}
              onToggleCategory={toggleCategory}
              itemPadding={20}
            />
          </aside>
        </>
      )}

      {/* ── Content ── */}
      <main ref={contentRef} style={{ 
        flex: 1, 
        padding: isMobile 
          ? 'clamp(16px, 5vw, 40px) clamp(12px, 4vw, 48px)' 
          : '40px 48px', 
        maxWidth: isMobile ? '100%' : 820, 
        width: '100%',
      }}>
        
        {/* Mobile page label (static — the actual nav trigger is the
            always-visible floating button below, which uses `position:
            fixed` instead of `sticky` for the same robustness reason as
            the desktop nav above). */}
        {isMobile && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: 12,
            marginBottom: 16,
            borderBottom: '1px solid var(--border)',
          }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--primary)' }}>
              Developer Guide
            </div>
          </div>
        )}

        {/* Hero */}
        <div style={{ marginBottom: 'clamp(32px, 8vw, 48px)' }}>
          <div style={{ 
            fontSize: 'clamp(11px, 3.5vw, 13px)', 
            fontWeight: 700, 
            color: 'var(--primary)', 
            textTransform: 'uppercase', 
            letterSpacing: '0.8px', 
            marginBottom: 10 
          }}>
            Developer Guide
          </div>
          <h1 style={{ 
            margin: '0 0 12px', 
            fontSize: isMobile ? 'clamp(24px, 7vw, 32px)' : 32, 
            fontWeight: 900, 
            color: 'var(--text-dark)', 
            lineHeight: 1.2 
          }}>
            {isMobile ? 'Build your WhatsApp bot' : 'Build your WhatsApp bot\nin minutes.'}
          </h1>
          {!isMobile && (
            <p style={{ 
              margin: 0, 
              fontSize: 15, 
              color: 'var(--text-muted)', 
              lineHeight: 1.7, 
              maxWidth: 560 
            }}>
              Everything you need to create, configure, and launch a fully working WhatsApp bot — from registering your account to handling WhatsApp Flows with end-to-end encryption.
            </p>
          )}
        </div>

        {isMobile && (
          <p style={{ 
            margin: '0 0 24px', 
            fontSize: 'clamp(13px, 4vw, 15px)', 
            color: 'var(--text-muted)', 
            lineHeight: 1.7 
          }}>
            Everything you need to create, configure, and launch a fully working WhatsApp bot — from registering your account to handling WhatsApp Flows with end-to-end encryption.
          </p>
        )}

        <button
          onClick={downloadMarkdown}
          title="Download this entire guide as a single Markdown file — handy for pasting into an AI assistant or keeping offline"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 'clamp(24px, 6vw, 36px)',
            padding: '10px 18px',
            borderRadius: 10,
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            color: 'var(--text-dark)',
            fontWeight: 600,
            fontSize: 'clamp(12px, 3.5vw, 13px)',
            cursor: 'pointer',
          }}
        >
          <span>📥</span> Download this guide as .md
        </button>

        {/* ── 1. Overview ── */}
        <SectionHeading id="overview" icon="🗺️" title="Overview" subtitle="What Wabot does and how the pieces fit together." />

        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.8, 
          marginBottom: 20 
        }}>
          Wabot connects to the <strong>Meta WhatsApp Business API</strong> on your behalf. You write the bot logic, we handle the infrastructure — webhook routing, message delivery, session management, and encrypted flow data exchange.
        </p>

        <div style={cardGridStyle}>
          {[
            { icon: '🤖', title: 'Multiple Bots', desc: 'Manage as many WhatsApp bots as you need from one dashboard.' },
            { icon: '⌨️', title: 'Commands', desc: 'Write bot logic in JavaScript, Python, or JSON. Triggered by messages.' },
            { icon: '🌊', title: 'WhatsApp Flows', desc: 'Build interactive forms, sign-ups, surveys, and order flows inside WhatsApp.' },
            { icon: '🗂️', title: 'Panel', desc: 'A key/value config store for your bot — store API keys, settings, and more.' },
          ].map(f => <Card key={f.title} icon={f.icon} title={f.title}>{f.desc}</Card>)}
        </div>

        <InfoBox type="info">
          You need a <strong>Meta Business Account</strong> with a WhatsApp Business App to use this platform. If you don't have one yet, <a href="https://business.facebook.com" target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', fontWeight: 700 }}>create it here</a> — it's free.
        </InfoBox>

        {/* ── 2. Account Setup ── */}
        <SectionHeading id="setup" icon="⚙️" title="Account Setup" subtitle="Get your Meta credentials ready before creating a bot." />

        <Step number={1} title="Create a Meta Business App">
          Go to <a href="https://developers.facebook.com" target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', fontWeight: 600 }}>developers.facebook.com</a> → <strong>My Apps → Create App</strong> → choose <strong>Business</strong> type. Add the <strong>WhatsApp</strong> product to your app.
        </Step>

        <Step number={2} title="Get your Phone Number ID and WABA ID">
          Inside your Meta App → <strong>WhatsApp → API Setup</strong>. You'll see:
          <br /><br />
          <Table
            headers={['Field', 'Where to find it']}
            rows={[
              ['Phone Number ID', 'WhatsApp → API Setup → "From" phone number section'],
              ['WABA ID', 'WhatsApp → API Setup → "WhatsApp Business Account" line'],
              ['Access Token', 'WhatsApp → API Setup → Temporary or permanent token'],
              ['App Secret', 'App Settings → Basic → App Secret'],
            ]}
          />
        </Step>

        <Step number={3} title="Generate a permanent access token">
          The temporary token expires in 24 hours. For production, create a <strong>System User</strong> in Meta Business Manager with a permanent token that has these permissions:
          <br /><br />
          <Tag>whatsapp_business_messaging</Tag>{' '}
          <Tag>whatsapp_business_management</Tag>
        </Step>

        <Step number={4} title="Register and verify your Wabot account">
          Sign up at <strong>/register</strong>, verify your email, then log in. You're ready to create your first bot.
        </Step>

        {/* ── 3. Create a Bot ── */}
        <SectionHeading id="create-bot" icon="🤖" title="Create a Bot" subtitle="Connect your Meta credentials and go live." />

        <Step number={1} title="Click + New Bot on the Dashboard">
          From <strong>/dashboard</strong>, click the <strong>+ New Bot</strong> button.
        </Step>

        <Step number={2} title="Fill in your bot details">
          <Table
            headers={['Field', 'Description']}
            rows={[
              ['Name', 'Your bot\'s display name — shown in the dashboard'],
              ['WhatsApp Token', 'The access token from Meta (permanent recommended)'],
              ['Phone Number ID', 'From Meta → WhatsApp → API Setup'],
              ['WABA ID', 'Your WhatsApp Business Account ID'],
              ['App Secret', 'From Meta → App Settings → Basic'],
              ['Meta App ID', 'Optional. Only needed if you plan to upload a Business Profile photo (see the Business Profile section below). Find it under Meta for Developers → your app → Settings → Basic → "App ID".'],
              ['Language', 'Default language for new commands (JavaScript recommended)'],
            ]}
          />
          <InfoBox type="tip">
            New to this? "WABA ID", "App Secret", and "Meta App ID" all come from the same place: your app inside{' '}
            <strong>Meta for Developers</strong> (developers.facebook.com). If you don't have any of these yet, finish the{' '}
            <strong>Account Setup</strong> section above first — it walks through creating that app from scratch.
          </InfoBox>
        </Step>

        <Step number={3} title="Set up your Meta webhook">
          In your Meta App → <strong>WhatsApp → Configuration → Webhook</strong>, set:
          <br /><br />
          <CodeBlock label="Webhook URL">https://www.cmdkit.im/api/webhook/whatsapp/</CodeBlock>
          This URL is the <strong>same for every bot</strong> — you do not add a Bot ID to it. CommandKit routes each message to the right bot automatically using the Phone Number ID.
          <br /><br />
          For the <strong>Verify Token</strong>: find it in your bot sidebar → <strong>Settings → Webhook Token</strong>, copy and paste it into Meta's Verify Token field.
          <br /><br />
          Under <strong>Webhook Fields → Manage</strong>, subscribe to: <Tag>messages</Tag>{' '}<Tag>message_deliveries</Tag>{' '}<Tag>message_reads</Tag>
        </Step>

        <Step number={4} title="Test it">
          Send any message to your WhatsApp number. If you see it appear in the <strong>Logs</strong> tab → your bot is live. 🎉
        </Step>

        <InfoBox type="tip">
          Your <strong>Bot ID</strong> is shown in the Overview tab under "Bot Details". You'll need it in your webhook URL and any direct API calls.
        </InfoBox>

        {/* ── WhatsApp Health ── */}
        <SectionHeading
          id="health"
          icon="💚"
          title="WhatsApp Health"
          subtitle="A quick dashboard showing whether Meta considers your number healthy right now."
        />

        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.8, marginBottom: 16 }}>
          Meta scores every WhatsApp Business number on things like{' '}
          <strong>message quality</strong> and how many people it can message per day. If you're new to this: think of
          it like a "trust score" — the more people who reply and engage positively with your number, the higher your
          score, and the more you're allowed to send. If people block or report you, the score drops, and Meta can
          limit or shut off your number. The <strong>WhatsApp Health</strong> card lives on your bot's{' '}
          <strong>Overview tab</strong>, and shows you this score without needing to log into Meta's own dashboard.
        </p>

        <div style={cardGridStyle}>
          {[
            { icon: '🟢', title: 'Quality Rating', desc: 'GREEN, YELLOW, or RED. GREEN is healthy. YELLOW means engagement is slipping. RED means Meta may restrict how many messages you can send — treat it as urgent.' },
            { icon: '📶', title: 'Messaging Tier', desc: 'How many people your number can start a conversation with in a rolling 24-hour window (e.g. 250, 1K, 10K, 100K, or Unlimited). This goes up automatically as your quality stays good.' },
            { icon: '📞', title: 'Phone Number', desc: 'The actual WhatsApp number tied to this bot, exactly as Meta has it on file — useful for double-checking you\'re looking at the right bot.' },
            { icon: '🪪', title: 'Display Name Status', desc: 'Whether the business name shown next to your number (e.g. "PocketFi") is APPROVED, PENDING, or DECLINED by Meta.' },
          ].map(f => <Card key={f.title} icon={f.icon} title={f.title}>{f.desc}</Card>)}
        </div>

        <Step number={1} title="Open the Health card">
          Go to your bot → <strong>Overview</strong> tab. The <strong>WhatsApp Health</strong> card sits right next to
          your API Credentials card. It loads automatically the first time you open the tab.
        </Step>
        <Step number={2} title="Refresh when you need the latest numbers">
          Click <strong>↻ Refresh</strong> to pull a fresh check from Meta. To avoid hammering Meta's servers, the
          card caches its result for about 10 minutes — clicking Refresh forces an immediate live check instead of
          showing you the cached one.
        </Step>
        <Step number={3} title="Watch for the 'changed' flag">
          If your Quality Rating or Messaging Tier changed since the last time you checked, you'll see a small{' '}
          <strong>changed</strong> badge and a pop-up notice — for example if you dropped from GREEN to YELLOW. This
          is your early warning sign that something needs attention (see the tip box below).
        </Step>

        <InfoBox type="tip">
          <strong>What do I do if my rating drops?</strong> A drop to YELLOW or RED almost always means your messages
          are being marked as spam, ignored, or blocked more than usual. Slow down how many messages you send, make
          sure people actually opted in to hear from your bot, and avoid sending the same promotional message
          repeatedly to people who haven't replied in a while.
        </InfoBox>

        <InfoBox type="info">
          <strong>Why does "Messaging Tier" sometimes show —?</strong> Meta occasionally changes which API field this
          number comes from. If you ever see a blank tier where you expect a number, it usually resolves itself on
          the next refresh — Meta's side, not something wrong with your bot setup.
        </InfoBox>

        {/* ── Business Profile ── */}
        <SectionHeading
          id="profile"
          icon="🪪"
          title="Business Profile"
          subtitle="The public-facing info customers see when they open a chat with your number."
        />

        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.8, marginBottom: 16 }}>
          This is the same "About this business" card you see when you open any WhatsApp Business chat — your
          business's about line, description, address, contact email, website links, industry, and photo. You can
          view and edit all of it right from the <strong>Business Profile</strong> card on your bot's{' '}
          <strong>Overview tab</strong>, without needing to use the WhatsApp Manager website.
        </p>

        <Table
          headers={['Field', 'What it is', 'Limit']}
          rows={[
            ['About', 'A short status line shown right under your business name (like a WhatsApp status)', '139 characters'],
            ['Description', 'A longer paragraph describing what your business does', '256 characters'],
            ['Address', 'Your physical business address, if you have one customers should know about', '256 characters'],
            ['Contact Email', 'An email address customers can reach you at', '128 characters'],
            ['Industry', 'A category from Meta\'s list (Finance, Retail, Education, etc.)', 'Pick from dropdown'],
            ['Websites', 'Up to two links — your main site, a social page, anything relevant', '2 links, 256 characters each'],
          ]}
        />

        <Step number={1} title="Open the Business Profile card">
          Go to your bot → <strong>Overview</strong> tab, find the <strong>Business Profile</strong> card, and click{' '}
          <strong>Edit</strong>.
        </Step>
        <Step number={2} title="Fill in what you want customers to see">
          You don't have to fill in every field. Even just an About line and a Description makes your business look
          complete and trustworthy compared to a blank profile.
        </Step>
        <Step number={3} title="Upload a photo (optional)">
          Click <strong>Change Photo</strong> and pick a JPEG or PNG under 5MB. This needs the{' '}
          <strong>Meta App ID</strong> field to be filled in on your bot (see the Create a Bot section above) — if it
          isn't set yet, the card will tell you exactly that instead of failing silently.
        </Step>
        <Step number={4} title="Save">
          Click <strong>Save Changes</strong>. Text fields update instantly. A new photo can take Meta a minute or two
          to show up everywhere (your own device may still show the old one briefly due to caching).
        </Step>

        <InfoBox type="warning">
          Website links must start with <code>http://</code> or <code>https://</code> — a bare address like{' '}
          <code>mybusiness.com</code> without the <code>https://</code> in front will be rejected.
        </InfoBox>

        {/* ── 4. Commands ── */}
        <SectionHeading id="commands" icon="⌨️" title="Commands" subtitle="Commands are the logic that runs when your bot receives a message." />

        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.8, 
          marginBottom: 20 
        }}>
          Go to your bot → <strong>Commands</strong> tab → click <strong>+ New Command</strong>. Every command has three things: a <strong>trigger</strong> (what makes it fire), a <strong>match type</strong> (how the trigger is compared against incoming messages), and a list of <strong>actions</strong> (what happens when it fires).
        </p>

        <p style={{ 
          fontSize: 'clamp(14px, 4vw, 15px)', 
          fontWeight: 800, 
          color: 'var(--text-dark)', 
          marginBottom: 10 
        }}>1. Choose a match type</p>
        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 10 
        }}>
          This controls how your <strong>Trigger</strong> field is compared to the message the user sends:
        </p>
        <Table
          headers={['Match type', 'How it matches', 'Example trigger']}
          rows={[
            ['Exact', 'Message must equal the trigger exactly (case-insensitive)', '/start'],
            ['Contains', 'Fires if the trigger text appears anywhere in the message', 'order'],
            ['Regex', 'Trigger is a regular expression. Capture groups become {{match.1}}, {{match.2}}, etc.', 'order #(\\d+)'],
            ['Wildcard', 'Catch-all fallback — fires only when nothing else matches. Leave trigger as * or empty', '*'],
          ]}
        />
        <InfoBox type="tip">
          When more than one command could match, <strong>Exact</strong> is checked first, then <strong>Regex</strong>, then <strong>Contains</strong>, then <strong>Wildcard</strong> last. So a specific command always wins over a catch-all, regardless of the order you created them in.
        </InfoBox>

        <p style={{ 
          fontSize: 'clamp(14px, 4vw, 15px)', 
          fontWeight: 800, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 28 
        }}>2. Draft vs. Live — publishing commands</p>
        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 14 
        }}>
          Every command starts as a <strong>Draft</strong> when you create or save it — it will <strong>never run</strong> on real incoming messages until you publish it. This means you can build and rework a command without worrying about half-finished logic going live by accident.
        </p>
        <Table
          headers={['Status', 'What it means']}
          rows={[
            ['Draft', 'Saved, but ignored by incoming WhatsApp messages. Safe to edit freely.'],
            ['Live', 'Active — runs whenever its trigger matches an incoming message.'],
          ]}
        />
        <InfoBox type="warning">
          Editing a <strong>Live</strong> command automatically moves it back to <strong>Draft</strong> when you save. This is intentional — it stops an in-progress edit from going live mid-save. Click <strong>Publish</strong> again once you're happy with the change.
        </InfoBox>

        <p style={{ 
          fontSize: 'clamp(14px, 4vw, 15px)', 
          fontWeight: 800, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 28 
        }}>3. Test before you publish</p>
        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 14 
        }}>
          Open any command → <strong>Test</strong> tab. Type a sample message and click <strong>Run test</strong>. This simulates the message hitting your command's actions and shows you exactly what would be sent back — <strong>without actually sending anything to WhatsApp</strong>. It's the fastest way to check your logic before going live.
        </p>
        <InfoBox type="info">
          The test sandbox currently supports <strong>JSON action</strong> commands. It also resolves regex capture groups, so if your trigger is a regex, <code>{'{{match.1}}'}</code> etc. will behave the same in test mode as they will in production.
        </InfoBox>
        <InfoBox type="warning">
          Test mode does <strong>not</strong> fake your <code>http_post</code> / <code>http_get</code> actions — those still call the real URL you configured. If a command calls a live API (e.g. a payment or verification endpoint), keep that in mind while testing repeatedly.
        </InfoBox>

        <p style={{ 
          fontSize: 'clamp(14px, 4vw, 15px)', 
          fontWeight: 800, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 28 
        }}>4. Writing actions (JSON language)</p>
        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 10 
        }}>
          For most bots, you don't need to write code at all — set the command's <strong>Language</strong> to <strong>JSON</strong> and define a list of actions that run top to bottom. Here's every action type available:
        </p>
        <Table
          headers={['Action type', 'What it does']}
          rows={[
            ['send_text', 'Sends a plain text WhatsApp message'],
            ['send_list', 'Sends an interactive list menu — a scrollable set of options the user taps to select'],
            ['send_buttons', 'Sends a message with up to 3 quick-reply buttons'],
            ['send_cta_url', 'Sends a message with a single external-link ("Call To Action") button that opens a URL'],
            ['request_payment', "Sends a payment link (via your bot's connected PocketFi merchant) as a CTA button — for a saved product or a custom one-off amount"],
            ['send_template', 'Sends an approved WhatsApp template message — required once you\'re outside the 24-hour customer service window'],
            ['send_reaction', "Reacts to the user's message with an emoji"],
            ['send_image', 'Sends an image from a URL with an optional caption'],
            ['send_document', 'Sends a file/document from a URL'],
            ['http_post', 'POSTs JSON to an external API. Response fields accessible via {{result.field}} or {{save_as_name.field}}'],
            ['http_post_form', 'POSTs application/x-www-form-urlencoded instead of JSON — for APIs that expect classic form data'],
            ['http_get', 'GETs from an external API. Same response access as http_post'],
            ['set_var', 'Sets a temporary variable for the rest of this run only — gone when the message finishes'],
            ['set_storage', 'Saves a value permanently for this user — persists across all future messages'],
            ['delete_storage', 'Deletes a stored value for this user'],
            ['if', 'Branches into then / else based on a single "{{left}} OP right" condition'],
            ['if_else', 'Like if, but with more operators — contains, starts_with, ends_with, is_empty, is_not_empty — via explicit left/op/right fields'],
            ['for_each', 'Loops over an array, running a set of actions per item — exposes {{loop.item}} and {{loop.index}}'],
            ['jump', 'Hands off to another command by trigger name'],
            ['conversation_wait', "Pauses and waits for the user's next message, then resumes a specific command at a named step"],
            ['conversation_clear', 'Cancels any active conversation_wait state for this user'],
            ['route_flow', 'Extracts data submitted from a WhatsApp Flow into {{flow.field}} variables'],
            ['send_flow', 'Sends an interactive WhatsApp Flow message'],
            ['send_raw', 'Sends any raw WhatsApp API payload — for advanced use cases'],
            ['wait', 'Pauses execution for a number of seconds (capped at 5) before the next action runs'],
            ['log', 'Writes a message to this bot\'s Logs tab at a chosen level — useful for debugging JSON commands'],
          ]}
        />

        <p style={{ fontSize: 'clamp(13px, 4vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10, marginTop: 28 }}>
          send_list — building interactive menus
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          <code>send_list</code> is how you build a scrollable menu. The user taps an option and WhatsApp sends its <code>id</code> back as the next message — which triggers the matching command:
        </p>
        <CodeBlock label="send_list — full structure">
{`{
  "type": "send_list",
  "header": "Main Menu",
  "body": "What would you like to do today?",
  "footer": "Tap an option to continue",
  "button": "View Options",
  "sections": [
    {
      "title": "Account",
      "rows": [
        {
          "id": "/balance",
          "title": "Check Balance",
          "description": "View your current wallet balance"
        },
        {
          "id": "/transactions",
          "title": "Transaction History",
          "description": "See your last 10 transactions"
        }
      ]
    },
    {
      "title": "Support",
      "rows": [
        {
          "id": "/support",
          "title": "Talk to Agent",
          "description": "Connect with a live support agent"
        }
      ]
    }
  ]
}`}
        </CodeBlock>
        <Table
          headers={['Field', 'Required', 'Description']}
          rows={[
            ['body', 'Yes', 'Main message text'],
            ['button', 'Yes', 'Label on the button that opens the list — max 20 characters'],
            ['sections', 'Yes', 'Array of section groups. Each has a title and rows array'],
            ['rows[].id', 'Yes', 'Text WhatsApp sends back when user taps — must match an exact command trigger'],
            ['rows[].title', 'Yes', 'Display label — max 24 characters'],
            ['rows[].description', 'No', 'Subtitle shown under the title — max 72 characters'],
            ['header', 'No', 'Bold text shown above the body'],
            ['footer', 'No', 'Small grey text shown below the body'],
          ]}
        />
        <InfoBox type="warning">
          The <code>id</code> of each row is what gets sent back as the user's message when they tap it. It must exactly match the trigger of a live command — including the leading slash (e.g. <code>/balance</code> not <code>balance</code>).
        </InfoBox>

        <p style={{ fontSize: 'clamp(13px, 4vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10, marginTop: 28 }}>
          send_cta_url — adding a link (CTA) button
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          <code>send_cta_url</code> sends a message with a single button that opens a URL — it renders with an external-link icon, and is what WhatsApp calls a <strong>"CTA URL" button</strong>. Use it for pricing pages, checkout links, docs, or any destination outside WhatsApp. Add it to a command's actions like this:
        </p>
        <CodeBlock label="send_cta_url — full structure">
{`{
  "type": "send_cta_url",
  "body": "Check out our pricing!",
  "display_text": "View Pricing",
  "url": "https://yourdomain.com/pricing",
  "header": "",
  "footer": ""
}`}
        </CodeBlock>
        <Table
          headers={['Field', 'Required', 'Description']}
          rows={[
            ['body', 'Yes', 'Main message text shown above the button'],
            ['url', 'Yes', 'Destination the button opens. Supports {{placeholders}}'],
            ['display_text', 'No', 'Text on the button itself — max 20 characters (longer text is cut off). Defaults to "Open Link"'],
            ['header', 'No', 'Bold text shown above the body'],
            ['footer', 'No', 'Small grey text shown below the body'],
          ]}
        />
        <InfoBox type="warning">
          A <code>send_cta_url</code> message is <strong>always exactly one button</strong> — that's a WhatsApp platform rule, not a limit we impose. You can't combine it with <code>send_buttons</code> (quick-reply buttons) in the same message. If you need both a link and quick replies, send them as two separate actions/messages.
        </InfoBox>
        <InfoBox type="tip">
          <code>display_text</code>, <code>body</code>, and <code>url</code> all resolve <code>{'{{placeholders}}'}</code> — so you can build a per-user link, e.g. <code>{'"url": "https://yourdomain.com/checkout?ref={{chat_id}}"'}</code>.
        </InfoBox>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          To wire it into a menu, point a button or list row at the command that contains the <code>send_cta_url</code> action — same as any other command:
        </p>
        <CodeBlock label="Command with trigger 'pricing' — JSON actions">
{`[
  {
    "type": "send_cta_url",
    "body": "Here's our current pricing 👇",
    "display_text": "View Pricing",
    "url": "https://yourdomain.com/pricing"
  }
]`}
        </CodeBlock>

        <p style={{ fontSize: 'clamp(13px, 4vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10, marginTop: 28 }}>
          request_payment — collecting payment inside the chat
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          <code>request_payment</code> starts a checkout using <strong>your own connected PocketFi merchant account</strong> for this bot — never the platform's — and sends the resulting payment link to the customer as a <code>cta_url</code> button. You need to connect PocketFi for this bot first, from your bot's <strong>PocketFi</strong> tab.
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          There are two ways to use it — point it at a saved product, or charge a one-off custom amount:
        </p>
        <CodeBlock label="Option A — charge for a saved product">
{`{
  "type": "request_payment",
  "product_id": "PRD1a2b3c4d5e",
  "body": "Tap below to complete your order:"
}`}
        </CodeBlock>
        <CodeBlock label="Option B — one-off custom amount (no product needed)">
{`{
  "type": "request_payment",
  "description": "2x Blue Sneakers",
  "amount": "15000",
  "body": "Tap below to complete your order:"
}`}
        </CodeBlock>
        <Table
          headers={['Field', 'Required', 'Description']}
          rows={[
            ['product_id', 'Only for Option A', 'ID of a product saved under this bot — see the Products tab. If set, its saved name and price are used automatically.'],
            ['amount', 'Only for Option B', 'Custom charge amount in Naira. Must be greater than zero.'],
            ['description', 'No', 'Label shown for the charge — falls back to the product name if product_id is used'],
            ['body', 'No', 'Message text shown above the payment button'],
            ['button_text', 'No', 'Text on the button — defaults to "Pay ₦{amount}"'],
          ]}
        />
        <InfoBox type="warning">
          If <code>amount</code> resolves to zero or less, or a given <code>product_id</code> isn't found (or is inactive), <code>request_payment</code> silently does nothing and logs a warning — the customer sees no message at all. Check the Logs tab if a payment command seems to "do nothing."
        </InfoBox>
        <InfoBox type="tip">
          Since <code>request_payment</code> sends its link as a <code>cta_url</code> button under the hood, the same rule applies: it's always a single button, and can't be combined with <code>send_buttons</code> in the same message.
        </InfoBox>

        <p style={{ fontSize: 'clamp(13px, 4vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10, marginTop: 28 }}>
          http_post — calling external APIs
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          Use <code>http_post</code> to call any external API. The response is automatically parsed and available as placeholders in all subsequent actions in the same run:
        </p>
        <CodeBlock label="http_post — full structure">
{`{
  "type": "http_post",
  "url": "https://yourapi.com/verify/",
  "headers": {
    "Authorization": "Bearer {{storage.userToken}}",
    "Content-Type": "application/json"
  },
  "body": {
    "phone": "{{chat_id}}",
    "type":  "verify"
  },
  "save_as": "reg_check"
}`}
        </CodeBlock>
        <Table
          headers={['Field', 'Required', 'Description']}
          rows={[
            ['url', 'Yes', 'API endpoint to POST to. Supports {{panel.key}} placeholders.'],
            ['body', 'No', 'JSON object sent as request body. All values support {{placeholders}}.'],
            ['headers', 'No', 'Custom HTTP headers. Values fully support {{placeholders}} — e.g. an Authorization header built from a token you saved earlier with set_storage. This resolves identically for http_post, http_post_form, and http_get.'],
            ['save_as', 'No', 'Name to store the response. If "reg_check", access fields as {{reg_check.status}}, {{reg_check.name}}, etc. — nesting works too, e.g. {{reg_check.data.token}}.'],
          ]}
        />
        <CodeBlock label="Reading the response — correct vs wrong">
{`// ✅ CORRECT — read response in the SAME command run
[
  {
    "type": "http_post",
    "url": "https://api.example.com/check",
    "body": { "phone": "{{chat_id}}" },
    "save_as": "reg_check"
  },
  {
    "type": "if",
    "condition": "{{reg_check.status}} == registered",
    "then": [{ "type": "jump", "trigger": "Menu" }]
  }
]

// ✅ Nested fields work at any depth, for any save_as name —
// e.g. if the API response looks like { "data": { "user": { "token": "..." } } }:
{ "type": "set_storage", "key": "userToken", "value": "{{reg_check.data.user.token}}" }

// ❌ WRONG — trying to read reg_check.* in a different command later
// It's gone after this message finishes.
// Save it to storage if you need it in a future command:
{ "type": "set_storage", "key": "userStatus", "value": "{{reg_check.status}}" }
// Then in any future command: {{storage.userStatus}} ✅`}
        </CodeBlock>
        <InfoBox type="warning">
          <strong>Most common bug:</strong> using <code>{'{{reg_check.field}}'}</code> (or whatever your <code>save_as</code> name is) in a command that runs on the user's <em>next</em> message. It will always be empty — a saved response only exists during the same message that made the API call. Save what you need with <code>set_storage</code> before the command ends.
        </InfoBox>
        <InfoBox type="tip">
          <strong>"result" is special.</strong> Whatever you name <code>save_as</code>, you can <em>also</em> always reach the most recent HTTP call's response via <code>{'{{result.field}}'}</code> (or bare <code>{'{{result}}'}</code> for the whole thing) — regardless of what you actually called <code>save_as</code>. This is handy for a quick debug <code>log</code> line without worrying about the exact name, but for anything you're keeping around, prefer your real <code>save_as</code> name — it's clearer to read back later, and avoids surprises if a later action in the same run makes a second HTTP call and overwrites what <code>result</code> points to.
        </InfoBox>

        <p style={{ fontSize: 'clamp(13px, 4vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10, marginTop: 20 }}>
          http_post_form — when an API wants form data instead of JSON
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          Some external APIs (older ones especially) expect a classic <code>application/x-www-form-urlencoded</code> body instead of JSON. <code>http_post_form</code> is identical to <code>http_post</code> in every field — <code>url</code>, <code>body</code>, <code>headers</code>, <code>save_as</code> — it just changes how the body is encoded on the wire:
        </p>
        <CodeBlock label="http_post_form — same fields as http_post">
{`{
  "type": "http_post_form",
  "url": "https://legacyapi.example.com/transactions",
  "body": { "phone": "{{chat_id}}", "action": "check" },
  "save_as": "result"
}`}
        </CodeBlock>

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 20 
        }}>Placeholders — pulling values into your actions</p>
        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 10 
        }}>
          Anywhere in an action's text you can use <code>{'{{...}}'}</code> placeholders. They get resolved before the action runs:
        </p>
        <Table
          headers={['Placeholder', 'Resolves to']}
          rows={[
            ['{{chat_id}}', 'The phone number / chat ID of the user who triggered this command'],
            ['{{name}}', 'The display name of the user, if available'],
            ['{{message}}', 'The raw text the user sent'],
            ['{{match.1}}, {{match.2}}…', 'Capture groups, only when the command\u2019s match type is Regex'],
            ['{{vars.key}}', 'A value previously set with set_var'],
            ['{{storage.key}}', 'A value previously set with set_storage for this user'],
            ['{{panel.key}}', 'A value from your bot\u2019s Panel / Config store'],
            ['{{reg_check.field}}', 'A field from a saved HTTP response — name matches whatever you passed to save_as'],
            ['{{reg_check.data.user.name}}', 'Nested fields work too, at any depth — no limit on how deeply you can dot into a response'],
            ['{{result.field}}', 'Always the MOST RECENT http_post/http_get/http_post_form response, regardless of what save_as name that call used'],
          ]}
        />
        <InfoBox type="info">
          <strong>Reserved names:</strong> <code>result</code>, <code>flow</code>, <code>conv</code>, <code>panel</code>, <code>storage</code>, and <code>var</code> all have
          special meaning as shown above — avoid using any of them as a <code>save_as</code> name for your own HTTP calls, since your
          response would then be shadowed by (or shadow) that reserved behavior. <code>{'{{flow.field}}'}</code> and <code>{'{{conv.field}}'}</code>
          specifically come from <code>route_flow</code> and conversation step state — they're always flat (one level), unlike
          HTTP response placeholders which support unlimited nesting.
        </InfoBox>

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 20 
        }}>Conditions — using the if action</p>
        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 10 
        }}>
          The <code>condition</code> field is a single string: <code>{'{{left}} OPERATOR right'}</code>. Supported operators: <code>==  !=  &gt;  &lt;  &gt;=  &lt;=</code>. Both sides are resolved through placeholders first, so you can compare a variable to a literal, or two variables to each other.
        </p>
        <CodeBlock label="if action example">
{`{
  "type": "if",
  "condition": "{{storage.isBanned}} == yes",
  "then": [
    { "type": "send_text", "text": "You are banned from using this bot." }
  ],
  "else": [
    { "type": "send_text", "text": "Welcome back!" }
  ]
}`}
        </CodeBlock>
        <InfoBox type="tip">
          You can nest <code>if</code> actions inside <code>then</code> / <code>else</code> as many levels deep as you need — this is how you build multi-step logic like "check banned → check registered → check logged in" in a single command.
        </InfoBox>

        <p style={{ fontSize: 'clamp(13px, 4vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10, marginTop: 20 }}>
          if_else — more comparison operators
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          <code>if</code> only understands <code>{'{{left}} OP right'}</code> as one string. When you need <code>contains</code>, <code>starts_with</code>, <code>ends_with</code>, or an empty/not-empty check, use <code>if_else</code> instead — it takes <code>left</code>, <code>op</code>, and <code>right</code> as separate fields:
        </p>
        <CodeBlock label="if_else — full structure">
{`{
  "type": "if_else",
  "left": "{{message}}",
  "op": "contains",
  "right": "cancel",
  "then": [
    { "type": "send_text", "text": "Order cancelled." },
    { "type": "conversation_clear" }
  ],
  "else": [
    { "type": "send_text", "text": "Got it, continuing..." }
  ]
}`}
        </CodeBlock>
        <Table
          headers={['op', 'Checks']}
          rows={[
            ['==  /  !=', 'Exact match / no match (strict, trimmed string comparison)'],
            ['>  <  >=  <=', 'Numeric comparison'],
            ['contains', 'left contains right as a substring'],
            ['starts_with / ends_with', 'left starts or ends with right'],
            ['is_empty / is_not_empty', 'Whether left is blank — right is ignored'],
          ]}
        />

        <p style={{ fontSize: 'clamp(13px, 4vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10, marginTop: 20 }}>
          for_each — looping over a list
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          Use <code>for_each</code> to run a set of actions once per item in an array — e.g. listing cart items or looping through an API response. Inside the loop, <code>{'{{loop.item}}'}</code> and <code>{'{{loop.index}}'}</code> are available, and if each item is itself an object, its fields are exposed as <code>{'{{loop.item.field}}'}</code>:
        </p>
        <CodeBlock label="for_each — full structure">
{`{
  "type": "for_each",
  "items": "{{result.cart_items}}",
  "actions": [
    {
      "type": "send_text",
      "text": "{{loop.index}}. {{loop.item.name}} — ₦{{loop.item.price}}"
    }
  ]
}`}
        </CodeBlock>
        <InfoBox type="tip">
          <code>items</code> can point at an array field from a saved HTTP response — nested or top-level, doesn't matter — as long as the
          field itself is exactly one <code>{'{{...}}'}</code> placeholder with nothing else in the string (as shown above). If you need to
          mix in literal text around it, wrap the whole thing as a JSON array string instead — e.g.
          <code>{'"items": "[{{loop_a}}, {{loop_b}}]"'}</code> — since a placeholder embedded in other text can only ever resolve to text, not a real list.
        </InfoBox>
        <InfoBox type="warning">
          <code>for_each</code> is capped at <strong>20 iterations</strong> regardless of how many items are in the array — anything beyond that is silently skipped (with a warning logged). Keep lists you loop over short, or paginate on your own API before sending them to the bot.
        </InfoBox>

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 20 
        }}>A complete real-world example</p>
        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 10 
        }}>
          This wildcard command checks if a user is registered before showing them anything else — a common "gatekeeper" pattern:
        </p>
        <CodeBlock label="JSON actions">
{`[
  {
    "type": "http_post",
    "url": "https://yourapi.com/verify/",
    "body": { "phone": "{{chat_id}}" },
    "save_as": "reg_check"
  },
  {
    "type": "if",
    "condition": "{{storage.isBanned}} == yes",
    "then": [
      { "type": "send_text", "text": "You're banned from using this bot." }
    ],
    "else": [
      {
        "type": "if",
        "condition": "{{reg_check.status}} != registered",
        "then": [
          {
            "type": "send_list",
            "body": "Welcome! You're not registered yet.",
            "button": "Get Started",
            "sections": [
              { "title": "Account", "rows": [
                { "id": "/register", "title": "Register Now" }
              ]}
            ]
          }
        ],
        "else": [
          { "type": "jump", "trigger": "Menu" }
        ]
      }
    ]
  }
]`}
        </CodeBlock>

        <p style={{ 
          fontSize: 'clamp(14px, 4vw, 15px)', 
          fontWeight: 800, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 28 
        }}>5. Templates & the 24-hour messaging window</p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 14 }}>
          WhatsApp only allows free-form messages (<code>send_text</code>, <code>send_list</code>, <code>send_buttons</code>, <code>send_cta_url</code>, images, etc.) within <strong>24 hours</strong> of the customer's last message to you. Outside that window, the only thing you're allowed to send is a pre-approved <strong>template message</strong> — this is a WhatsApp platform rule, not something we impose.
        </p>
        <Table
          headers={['Situation', 'What you can send']}
          rows={[
            ['Customer messaged you in the last 24 hours', 'Any action — send_text, send_list, send_buttons, send_cta_url, send_image, etc.'],
            ['More than 24 hours since their last message', 'Only send_template — everything else fails silently and gets logged as an error'],
          ]}
        />
        <InfoBox type="warning">
          This is the <strong>#1 cause</strong> of "my bot stopped replying to old customers" reports. If you're re-engaging someone (e.g. an abandoned cart reminder, a payment follow-up, a broadcast), check how long it's been — if it's outside 24 hours, you need <code>send_template</code>, not <code>send_text</code>.
        </InfoBox>

        <p style={{ fontSize: 'clamp(13px, 4vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10, marginTop: 20 }}>
          Creating a template
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          Templates are created and approved through Meta, not through commands. Go to Meta Business Manager → <strong>WhatsApp Manager → Message Templates → Create Template</strong>. Give it a name, a language, and a body with numbered placeholders like <code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>. Approval usually takes a few minutes to a few hours.
        </p>

        <p style={{ fontSize: 'clamp(13px, 4vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10, marginTop: 20 }}>
          Sending it — the send_template action
        </p>
        <CodeBlock label="send_template — full structure">
{`{
  "type": "send_template",
  "name": "deposit_confirmation",
  "lang": "en",
  "components": [
    {
      "type": "body",
      "parameters": [
        { "type": "text", "text": "{{name}}" },
        { "type": "text", "text": "₦{{storage.lastAmount}}" }
      ]
    }
  ]
}`}
        </CodeBlock>
        <Table
          headers={['Field', 'Required', 'Description']}
          rows={[
            ['name', 'Yes', 'The exact template name as approved in Meta Business Manager'],
            ['lang', 'No', 'Template language code — defaults to "en"'],
            ['components', 'No', 'Array matching the template\u2019s placeholders. Text parameter values support {{placeholders}}'],
          ]}
        />
        <InfoBox type="tip">
          The order of <code>parameters</code> must match the order of <code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>… in the approved template body — the API fills them in positionally, not by name.
        </InfoBox>

        <p style={{ 
          fontSize: 'clamp(14px, 4vw, 15px)', 
          fontWeight: 800, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 28 
        }}>6. Multi-step conversations — conversation_wait</p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 14 }}>
          Sometimes you need to ask the user a question and wait for their typed answer. <code>conversation_wait</code> pauses the current command and tells the bot to resume a specific command at a named step when the user sends their next message.
        </p>
        <Table
          headers={['Step', 'What happens']}
          rows={[
            ['1. Bot sends a question', '"What is your account number?"'],
            ['2. conversation_wait fires', 'Saves pending state: resume command X at step Y when user replies'],
            ['3. User types their answer', 'Wildcard fires — but the pending state intercepts it first'],
            ['4. Bot resumes your command', "Runs with {{conv.step}} = step name and {{message}} = user's answer"],
            ['5. conversation_clear', "Call this once you've used the answer to clear the pending state"],
          ]}
        />
        <CodeBlock label="conversation_wait — complete 2-step PIN example">
{`// Command trigger: /verify
[
  {
    "type": "if",
    "condition": "{{conv.step}} == enter_pin",
    "then": [
      {
        "_comment": "User just sent their PIN — process it",
        "type": "http_post",
        "url": "https://yourapi.com/verify-pin/",
        "body": { "phone": "{{chat_id}}", "pin": "{{message}}" },
        "save_as": "pin_check"
      },
      { "type": "conversation_clear" },
      {
        "type": "if",
        "condition": "{{result.status}} == ok",
        "then": [
          { "type": "set_storage", "key": "loginTrue", "value": "yes" },
          { "type": "jump", "trigger": "Menu" }
        ],
        "else": [
          { "type": "send_text", "text": "❌ Incorrect PIN. Try again." },
          { "type": "jump", "trigger": "/verify" }
        ]
      }
    ],
    "else": [
      {
        "_comment": "First visit — ask for PIN",
        "type": "send_text",
        "text": "🔐 Please enter your 4-digit PIN to continue:"
      },
      {
        "type": "conversation_wait",
        "command": "/verify",
        "step": "enter_pin"
      }
    ]
  }
]`}
        </CodeBlock>
        <InfoBox type="tip">
          The pattern is always the same: <strong>check <code>{'{{conv.step}}'}</code> first</strong>. If it matches → process the answer. If not → ask the question and set the wait. One command handles both sides cleanly.
        </InfoBox>
        <InfoBox type="warning">
          <code>conversation_wait</code> requires your <strong>Wildcard</strong> command to be live. The pending wait state intercepts the user's next message before the wildcard runs. If there's no wildcard, the wait state never resumes.
          Always call <code>conversation_clear</code> after consuming the answer — otherwise every future message keeps trying to resume the wait.
        </InfoBox>

        <p style={{ 
          fontSize: 'clamp(14px, 4vw, 15px)', 
          fontWeight: 800, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 28 
        }}>7. WhatsApp Flow responses — route_flow</p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 14 }}>
          When a user submits a WhatsApp Flow form, use <code>route_flow</code> as the first action to decrypt and parse the payload into <code>{'{{flow.field}}'}</code> variables:
        </p>
        <CodeBlock label="route_flow — extracting flow submission data">
{`[
  {
    "type": "route_flow",
    "save_to": "vars"
  },
  {
    "type": "if",
    "condition": "{{flow.action}} == create_ticket",
    "then": [
      {
        "type": "http_post",
        "url": "https://yourapi.com/tickets/",
        "body": {
          "issue": "{{flow.issue_text}}",
          "phone": "{{chat_id}}"
        },
        "save_as": "ticket_resp"
      },
      {
        "type": "send_text",
        "text": "✅ Ticket created! Reference: {{result.ticket_id}}"
      }
    ]
  }
]`}
        </CodeBlock>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          The field names in <code>{'{{flow.field}}'}</code> come from the <code>payload</code> object in your Flow JSON's <code>on-click-action</code>:
        </p>
        <CodeBlock label="Flow JSON payload defines the field names">
{`{
  "type": "Footer",
  "label": "Submit",
  "on-click-action": {
    "name": "complete",
    "payload": {
      "action":     "create_ticket",
      "issue_text": "\${form.issue_input}",
      "email":      "\${form.email_input}"
    }
  }
}
// After route_flow: {{flow.action}} = "create_ticket"
//                  {{flow.issue_text}} = what user typed
//                  {{flow.email}} = what user typed`}
        </CodeBlock>
        <InfoBox type="warning">
          <code>route_flow</code> must be the <strong>first action</strong> in your command when handling a flow submission. Reading <code>{'{{flow.field}}'}</code> before calling <code>route_flow</code> will always return empty.
        </InfoBox>
        <InfoBox type="tip">
          Always include an <code>action</code> field in your flow payload — it lets one command handle multiple flow types by branching on <code>{'{{flow.action}}'}</code>.
        </InfoBox>

        <p style={{ 
          fontSize: 'clamp(14px, 4vw, 15px)', 
          fontWeight: 800, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 28 
        }}>8. JavaScript / Python commands (advanced)</p>
        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 10 
        }}>
          If you need custom logic the JSON actions can't express, switch <strong>Language</strong> to JavaScript or Python and write a handler directly:
        </p>
        <CodeBlock label="JavaScript">
{`// Available globals: ctx, chat, storage, Panel, fetch

module.exports = async () => {
  await ctx.bot.sendMessage(chat.id, "Hello, " + chat.name + "!");

  const greeting = await Panel.get('custom_greeting');
  if (greeting) await ctx.bot.sendMessage(chat.id, greeting);

  await storage.set('last_seen', new Date().toISOString());
};`}
        </CodeBlock>

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10 
        }}>Available globals</p>
        <Table
          headers={['Global', 'What it gives you']}
          rows={[
            ['ctx.bot', 'Bot instance — sendMessage(), name, and other bot methods'],
            ['chat', 'Current chat — chat.id (phone number), chat.name, chat.message'],
            ['storage', 'Per-user key/value store — get(key), set(key, value), delete(key)'],
            ['Panel', 'Bot-wide config store — Panel.get(key)'],
            ['fetch', 'HTTP client — call external APIs'],
            ['options', 'Command options and parameters'],
          ]}
        />
        <InfoBox type="warning">
          The Test sandbox does not currently run JavaScript/Python handlers — only JSON action commands. Test those by sending a real message to your bot on WhatsApp instead.
        </InfoBox>

        <InfoBox type="tip">
          Use a <strong>wildcard</strong> command as a catch-all fallback. It only runs when no other live command matches the user's message.
        </InfoBox>

        {/* ── 5. Panel ── */}
        <SectionHeading id="panel" icon="🗂️" title="Panel / Config Store" subtitle="Store API keys, settings, and configuration values your commands can read at runtime." />

        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.8, 
          marginBottom: 20 
        }}>
          The Panel is a key/value store attached to your bot. Think of it as a settings page for your bot that your commands can read from. Go to your bot → <strong>Panel</strong> tab.
        </p>

        <Table
          headers={['Field type', 'Best used for']}
          rows={[
            ['text', 'Names, labels, short config values'],
            ['password', 'API keys, secrets (masked in the UI)'],
            ['textarea', 'Long text, templates, message templates'],
            ['number', 'Limits, counts, numeric settings'],
            ['url', 'Webhook URLs, API endpoints'],
            ['select', 'Fixed-choice settings (dropdown)'],
          ]}
        />

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 20 
        }}>Reading Panel values in a command</p>
        <CodeBlock label="JavaScript">
{`module.exports = async () => {
  const apiKey  = await Panel.get('stripe_api_key');
  const welcome = await Panel.get('welcome_message');

  await ctx.bot.sendMessage(chat.id, welcome || "Hello!");
};`}
        </CodeBlock>

        <InfoBox type="warning">
          Mark sensitive values (API keys, passwords) as <strong>Secret</strong> when creating the field. Secret fields are masked in the UI and never exposed to other users.
        </InfoBox>

        {/* ── 6. WhatsApp Flows ── */}
        <SectionHeading id="flows" icon="🌊" title="WhatsApp Flows" subtitle="Build interactive forms and guided journeys that run inside WhatsApp." />

        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.8, 
          marginBottom: 20 
        }}>
          WhatsApp Flows let you send a rich interactive experience — sign-up forms, appointment booking, order tracking, surveys — all within the WhatsApp chat. Go to your bot → <strong>Flows</strong> tab.
        </p>

        <InfoBox type="warning">
          Before creating flows, make sure your bot has a <strong>WABA ID</strong> set. Go to Overview → Edit Bot Settings to add it.
        </InfoBox>

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 14 
        }}>Flow lifecycle</p>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? 6 : 10, 
          marginBottom: 24, 
          flexWrap: 'wrap' 
        }}>
          {['Draft', '→', 'Fix JSON errors', '→', 'Publish', '→', 'Live to users', '→', 'Deprecate (retire)'].map((s, i) => (
            s === '→'
              ? <span key={i} style={{ color: 'var(--text-muted)', fontSize: isMobile ? 14 : 18 }}>→</span>
              : <span key={i} style={{
                  background: s === 'Draft' ? '#f3f4f6' : s === 'Live to users' ? 'rgba(0,196,140,0.12)' : s === 'Deprecate (retire)' ? 'rgba(255,59,48,0.08)' : 'var(--primary-light)',
                  color: s === 'Draft' ? '#374151' : s === 'Live to users' ? 'var(--success)' : s === 'Deprecate (retire)' ? 'var(--danger)' : 'var(--primary)',
                  fontSize: isMobile ? 'clamp(10px, 3.5vw, 12px)' : 12, 
                  fontWeight: 700, 
                  padding: `${isMobile ? '4px' : '5px'} ${isMobile ? '10px' : '12px'}`, 
                  borderRadius: 20,
                  whiteSpace: 'nowrap',
                }}>{s}</span>
          ))}
        </div>

        <InfoBox type="warning">
          ⚠️ Once a flow is <strong>published it cannot be edited</strong>. Make all changes while it is in Draft.
        </InfoBox>

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 24 
        }}>Minimum flow JSON structure</p>
        <CodeBlock label="flow.json">
{`{
  "version": "7.1",
  "screens": [
    {
      "id": "WELCOME",
      "title": "Welcome",
      "layout": {
        "type": "SingleColumnLayout",
        "children": [
          { "type": "TextHeading", "text": "Register your account" },
          { "type": "TextBody",    "text": "Fill in your details below." },
          {
            "type": "TextInput",
            "name": "full_name",
            "label": "Full Name",
            "input-type": "text",
            "required": true
          },
          {
            "type": "Footer",
            "label": "Submit",
            "on-click-action": {
              "name": "complete",
              "payload": { "name": "\${form.full_name}" }
            }
          }
        ]
      }
    }
  ]
}`}
        </CodeBlock>

        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7 
        }}>
          Paste your JSON into the Flow Editor, fix any errors shown in the <strong>Flow JSON Errors</strong> tab, then click <strong>Publish</strong>.
        </p>

        <p style={{ 
          fontSize: 'clamp(14px, 4vw, 15px)', 
          fontWeight: 800, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 28 
        }}>Understanding the JSON error checker</p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.8, marginBottom: 14 }}>
          The Flow JSON Errors tab checks your JSON <strong>before</strong> you publish, so you catch mistakes here
          instead of finding out from a confused customer later. It splits what it finds into two groups:
        </p>
        <div style={cardGridStyle}>
          <Card icon="⊗" title="Local schema check (blocking)">
            Real mistakes that will break the flow — a missing required field, a duplicate screen ID, or one of the
            two specific issues below. <strong>You cannot publish until these are all fixed.</strong>
          </Card>
          <Card icon="⚠️" title="Worth checking (non-blocking)">
            A heads-up about something that <em>usually</em> causes problems but might be intentional in your case.
            You can still publish with these showing — they're a caution, not a rule.
          </Card>
        </div>

        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10 }}>
          Three specific mistakes it catches (these are easy to make and hard to notice on your own):
        </p>
        <Table
          headers={['Mistake', 'Why it\'s a problem', 'The fix']}
          rows={[
            [
              <code key="1">init-value</code>,
              'Putting "init-value" directly on a TextInput (or any input) does nothing — WhatsApp quietly ignores it. Meta\'s own example screenshots sometimes show it this way, which is exactly why it\'s an easy trap to fall into.',
              <>Set initial values on the parent <code>Form</code> instead: <code>{'{"init-values": {"your_field": "..."}}'}</code></>,
            ],
            [
              <code key="2">action</code>,
              <>Naming a form field <code>action</code>, <code>screen</code>, <code>data</code>, <code>flow_token</code>, or <code>version</code> collides with names WhatsApp's own system already uses. Your field's value can get silently overwritten.</>,
              'Rename it — e.g. use "user_action" or "ticket_action" instead of just "action"',
            ],
            [
              'Multiple placeholders',
              <>A text block with two or more <code>{'${data.xxx}'}</code> placeholders in the same string often only fills in the first one, leaving the rest showing as literal text like <code>{'${data.email}'}</code> on the user\'s screen.</>,
              'Split it into two separate text components, one placeholder each',
            ],
          ]}
        />
        <InfoBox type="tip">
          Brand new to this? None of these three show up as "invalid JSON" — your file will still be syntactically
          correct. That's exactly why the checker exists: to catch mistakes that look fine but behave wrong once a
          real customer uses the flow.
        </InfoBox>

        {/* ── 7. Flow Endpoint ── */}
        <SectionHeading id="flow-endpoint" icon="🔗" title="Flow Endpoint" subtitle="Required for flows that collect data or need dynamic content from your server." />

        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.8, 
          marginBottom: 20 
        }}>
          When a user submits a flow, Meta calls your endpoint with an <strong>AES-encrypted payload</strong>. Your server decrypts it, processes the data, and sends back an encrypted response.
        </p>

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 14 
        }}>The 5-step checklist (Endpoint tab)</p>

        <Table
          headers={['Step', 'What to do']}
          rows={[
            ['✅ Set endpoint URI', 'Enter your server URL — or use the pre-built bot-level endpoint from Overview tab'],
            ['✅ Add phone number', 'Already done when you created the bot'],
            ['✅ Sign public key', 'Upload your RSA 2048-bit public key to Meta (one-time setup per WABA)'],
            ['✅ Connect Meta app', 'Already done — your Meta app is connected via the bot settings'],
            ['⬤ Health check', 'Run this to confirm Meta can reach and decrypt your endpoint'],
          ]}
        />

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 24 
        }}>Option A — Use the pre-built bot endpoint (easiest)</p>
        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 10 
        }}>
          Your bot already has a ready-made endpoint. Copy it from <strong>Overview → Flow Endpoint URI</strong>:
        </p>
        <CodeBlock label="Bot-level endpoint URI">https://www.cmdkit.im/api/bots/exchange/?bId=YOUR_BOT_ID</CodeBlock>
        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 20 
        }}>
          Paste this as the Endpoint URI on your flow. It handles Meta's health check pings automatically and routes submitted data to your bot commands.
        </p>

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10 
        }}>Option B — Use your own custom endpoint</p>
        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 10 
        }}>
          If you need custom logic (e.g. look up a database, call a third-party API), set up your own endpoint. Your server must handle Meta's encrypted payloads:
        </p>
        <CodeBlock label="Meta sends this to your endpoint (POST)">
{`{
  "encrypted_flow_data": "...",   // AES-encrypted payload
  "encrypted_aes_key":  "...",   // AES key encrypted with your RSA public key
  "initial_vector":     "..."    // IV for AES decryption
}`}
        </CodeBlock>

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10 
        }}>Your server must respond with:</p>
        <CodeBlock label="Health check response (HTTP 200)">
{`{ "data": { "status": "active", "version": "v3.0" } }`}
        </CodeBlock>

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 24 
        }}>Setting up your RSA key pair (one-time)</p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.8, marginBottom: 14 }}>
          Meta needs a public/private key pair to encrypt Flow data so only your endpoint can read it. You have two
          ways to get one — pick whichever fits you:
        </p>

        <div style={cardGridStyle}>
          <Card icon="✨" title="Generate Key Pair for Me (recommended)">
            No terminal, no openssl commands. One click creates a real key pair, uploads the public half to Meta for
            you automatically, and shows you both keys once so you can save your own copy if you ever need it.
            Covered in Step 1 below.
          </Card>
          <Card icon="⌨️" title="Generate it yourself">
            If you're comfortable with a terminal and want full control over where the private key lives from the
            start, you can still run the commands yourself and paste in just the public key. Covered as an
            alternative in Step 1 below.
          </Card>
        </div>

        <Step number={1} title="Get your key pair">
          Flows tab → any flow → <strong>Endpoint</strong> tab → click <strong>Sign public key</strong>. You'll see
          two buttons:
          <br /><br />
          <strong>Option A — Generate Key Pair for Me:</strong> click it, confirm, and you're done. Both keys are
          shown once on screen — save a copy if you want one, but for the setup below (the platform's own endpoint
          or a Custom Endpoint built here) you don't actually need to do anything with them.
          <br /><br />
          <strong>Option B — Upload Public Key (manual):</strong> if you'd rather generate it yourself first:
          <CodeBlock>
{`openssl genrsa -out private.pem 2048
openssl rsa -in private.pem -pubout -out public.pem`}
          </CodeBlock>
          then paste the contents of <code>public.pem</code> into the Upload Public Key box.
        </Step>
        <Step number={2} title="Run the health check">
          Once your endpoint is set, click <strong>Run Health check</strong>. If it passes you'll see <strong>Endpoint is active</strong> ✅. If it fails, check the <strong>Actual result</strong> and <strong>Resolution</strong> tabs for the exact error.
        </Step>

        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10, marginTop: 20 }}>
          Do I need to manually place my private key anywhere?
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.8, marginBottom: 14 }}>
          This trips people up, so here's the direct answer — it depends entirely on <strong>where your endpoint
          actually runs</strong>, not on which flow you set it up from (the key pair belongs to your whole bot, not
          to any one flow):
        </p>
        <Table
          headers={['Where your endpoint runs', 'Do you need to touch the private key?']}
          rows={[
            ['This platform\'s built-in Flow endpoint', 'No — fully automatic. The platform already has it and decrypts everything for you.'],
            ['A Custom Endpoint built inside this platform (Custom Endpoints tab)', 'No — also fully automatic. Your endpoint code receives already-decrypted, readable data. You never write decryption code yourself.'],
            ['Your own separate server, VPS, or other hosting entirely outside this platform', 'Yes — copy the private key from the reveal screen (or your saved copy) into that server\'s own files, since that server has no other way to get it.'],
          ]}
        />
        <InfoBox type="tip">
          If you're not sure which of these applies to you: if your Flow's <strong>Endpoint URI</strong> field points
          to a URL that starts with your own dashboard's domain (like <code>www.cmdkit.im/api/run/...</code> or{' '}
          <code>www.cmdkit.im/api/bots/exchange/...</code>), you're in one of the first two rows and don't need to do
          anything further. It's only the third row — a URL pointing somewhere else entirely — where you'd need to
          hand the private key over yourself.
        </InfoBox>

        <p style={{ 
          fontSize: 'clamp(14px, 4vw, 15px)', 
          fontWeight: 800, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 28 
        }}>Traffic Logs — see exactly what Meta sends your endpoint</p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.8, marginBottom: 14 }}>
          If your health check passes but real users still get stuck partway through a flow, the fastest way to find
          out why is to look at the actual traffic. Go to <strong>Flows</strong> tab → open your flow →{' '}
          <strong>Endpoint</strong> tab → <strong>View Logs</strong>. This shows every request Meta made to your
          endpoint, already decrypted for you, so you don't need to add your own logging code to see what's going wrong.
        </p>
        <Table
          headers={['Column', 'What it tells you']}
          rows={[
            ['Action', 'What kind of call this was — ping (health check), INIT (screen loading), data_exchange (a form was submitted), BACK (user went back a screen)'],
            ['Screen', 'Which screen of your flow this happened on'],
            ['Decrypted request', 'Exactly what Meta sent your server — the actual JSON, already decrypted so you can read it'],
            ['Response sent back', 'Exactly what your server replied with'],
            ['Routed to', 'Which of your endpoint URLs handled this call, and the HTTP status code it returned'],
            ['Error', 'If something went wrong, the exact error message — no need to dig through your own server logs first'],
          ]}
        />
        <InfoBox type="tip">
          New to debugging APIs? Think of Traffic Logs like a security camera for your endpoint — instead of guessing
          what might have gone wrong, you can see the exact message Meta sent and the exact reply your server gave,
          side by side.
        </InfoBox>

        {/* ── 8. Custom Endpoints ── */}
        <SectionHeading id="endpoints" icon="🧩" title="Custom Endpoints" subtitle="Write backend logic directly inside your bot — no separate server needed." />

        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.8, 
          marginBottom: 20 
        }}>
          Custom Endpoints give your bot its own webhook-style URLs — useful for a WhatsApp Flow's data exchange, payment gateway callbacks, CRMs, or any external system that needs to push data into your bot's logic. Go to your bot → <strong>Endpoints</strong> tab → <strong>+ New Endpoint</strong>.
        </p>

        <InfoBox type="tip">
          Using a Custom Endpoint as a WhatsApp Flow's data-exchange handler? You don't need to write any decryption
          code yourself, and you don't need to manually place a private key anywhere — the platform decrypts Meta's
          request before your code ever sees it, and your PHP just receives clean, already-readable JSON. See{' '}
          <strong>"Do I need to manually place my private key anywhere?"</strong> in the Flow Endpoint section for
          the full explanation.
        </InfoBox>

        <Step number="1" title="Create the endpoint">
          Give it a name (e.g. <code>check_transaction_status</code>) — this is just a label and doesn't affect the URL. Then pick a language:
          <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            <li style={{ marginBottom: 4 }}><strong>PHP</strong> — fully supported, runs your code on every call. Use this for anything with logic.</li>
            <li style={{ marginBottom: 4 }}><strong>JSON</strong> — a static document, returned exactly as written every time. No code runs.</li>
            <li style={{ marginBottom: 4 }}><strong>Other (plain text)</strong> — returned exactly as written, with a <code>text/plain</code> response.</li>
            <li><strong>JavaScript</strong> / <strong>Python</strong> — coming soon. You can save code now, but calls get a <code>501</code> response until the runtime ships.</li>
          </ul>
        </Step>

        <Step number="2" title="Write your PHP">
          Your code runs as the body of a request handler. <code>echo</code> whatever you want returned — it defaults to a JSON response with status <code>200</code>. Call <code>header(...)</code> and <code>http_response_code(...)</code> to customize either one, just like a normal PHP page.
        </Step>

        <Table
          headers={['Variable', 'Contains']}
          rows={[
            ['$method', 'HTTP method (GET, POST, etc.)'],
            ['$headers', 'Request headers'],
            ['$query', 'Query string parameters'],
            ['$body', 'Raw request body'],
            ['$json', 'Request body, pre-decoded as a PHP array (if it was JSON)'],
            ['$bot', "Your bot's info: bId, bName, bToken, bPhoneNumberId, bWhatsappId, bWebhookToken"],
          ]}
        />

        <CodeBlock label="Example endpoint.php">
{`<?php
header('Content-Type: application/json');

if ($json['ref'] ?? null) {
    $status = lookup_transaction($json['ref']); // your own logic
    echo json_encode(['status' => $status]);
} else {
    http_response_code(400);
    echo json_encode(['error' => 'Missing ref']);
}`}
        </CodeBlock>

        <Step number="3" title="Debug while you write">
          Call <code>debug_log('message', $data)</code> anywhere in your code — it shows up in this endpoint's <strong>Logs → Debug Log</strong> for that specific call, with a timestamp. No file access needed. If you already use plain <code>error_log(...)</code>, that's captured the same way. This is the fastest way to see what a request actually contained, or what a value resolved to, without guessing.
        </Step>

        <Step number="4" title="Save and copy the URL">
          Click <strong>Save Endpoint</strong>. The modal shows the endpoint's <strong>URL</strong> — copy it with the clipboard icon, then paste it into a WhatsApp Flow's <strong>Endpoint URI</strong> (if this endpoint handles a Flow's data exchange) or any external system's webhook settings.
        </Step>

        <CodeBlock label="Example endpoint URL">https://yourdomain.com/api/run/?id=your-unique-id</CodeBlock>

        <InfoBox type="tip">
          Keyboard shortcuts while editing: <strong>Ctrl/Cmd+S</strong> saves, <strong>Ctrl/Cmd+Z</strong> / <strong>Ctrl/Cmd+Y</strong> undo and redo. Saving with nothing changed tells you instead of doing a no-op round trip. Drag the bottom edge of the code box to resize it.
        </InfoBox>

        <Step number="5" title="Test it">
          Use the <strong>Test</strong> button on the endpoint's row to fire a quick GET request and see the raw response. It's a smoke check that the endpoint is reachable and returning <em>something</em> — for endpoints expecting a POST body (most Flow endpoints do), you'll still want a real call from the Flow itself, or a tool like Postman, to verify behavior against a realistic payload.
        </Step>

        <Step number="6" title="Toggle on/off as needed">
          Endpoints can be switched off without deleting them — handy while you're still setting up the integration on the other side. Regenerating the URL (🔄) invalidates the old one immediately; anything still pointing at it will break until you update it.
        </Step>

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10, 
          marginTop: 24 
        }}>Reading endpoint logs</p>
        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 10 
        }}>
          Open an endpoint → <strong>Logs</strong> tab to see every request it has received, including status, timestamp, headers and body. Endpoints that errored on their last call show a red <strong>⚠ View Logs</strong> badge on the row, so you can spot a broken integration at a glance without opening every endpoint.
        </p>
        <InfoBox type="tip">
          If a call isn't reaching your bot at all, check the endpoint's <strong>raw error log</strong> first (a cPanel-style <code>error_log</code> file) — this catches issues that happen before your logic even runs, like malformed requests or server-level errors.
        </InfoBox>

        <InfoBox type="warning">
          If this endpoint is wired into a Flow's data exchange, make sure the <code>screen</code> values you check for in your PHP (e.g. <code>$json['screen'] === '...'</code>) match the screen IDs actually defined in that Flow's JSON. These can drift apart silently if either one is edited later — there's no automatic check linking them yet, so a mismatch shows up as a confusing runtime error rather than a clear validation message.
        </InfoBox>

        <InfoBox type="warning">
          Custom Endpoints are different from the <strong>Flow Endpoint</strong> above — Flow Endpoints are specifically for Meta's encrypted WhatsApp Flow data exchange. Custom Endpoints are general-purpose URLs for your own integrations and don't use Meta's encryption scheme.
        </InfoBox>

        {/* ── 9. Users & Logs ── */}
        <SectionHeading id="users-logs" icon="📊" title="Users & Logs" subtitle="Monitor who's using your bot and what's happening at runtime." />

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10 
        }}>Users tab</p>
        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 16 
        }}>
          Shows every WhatsApp user who has interacted with your bot — their name, chat ID (phone number), and last seen time. Use the search box to find a specific user.
        </p>

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10 
        }}>Logs tab</p>
        <Table
          headers={['Level', 'Colour', 'Use']}
          rows={[
            ['debug', '⬤ Grey', 'Verbose dev info — normal operation details'],
            ['info', '⬤ Blue', 'Successful events — message received, command ran'],
            ['warn', '⬤ Amber', 'Non-fatal issues — slow response, retried call'],
            ['error', '⬤ Red', 'Failures — uncaught exception, API error'],
          ]}
        />

        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 10 
        }}>
          Filter by level using the dropdown. Click any log row to expand the full payload. Use <strong>Clear logs</strong> to reset.
        </p>

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10 
        }}>Writing logs from your commands</p>
        <CodeBlock label="JavaScript">
{`module.exports = async () => {
  ctx.log.info('Payment initiated', { userId: chat.id, amount: 500 });
  ctx.log.error('Stripe API failed', { error: err.message });
};`}
        </CodeBlock>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          For JSON commands, use the <code>log</code> action instead:
        </p>
        <CodeBlock label="JSON — log action">
{`{ "type": "log", "level": "debug", "message": "checking status: {{reg_check.status}}" }`}
        </CodeBlock>

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10,
          marginTop: 24,
        }}>Debugging a command — reading what the Logs tab actually shows you</p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          Two things happen automatically, without you adding a single <code>log</code> action — search the Logs tab by the user's phone
          number and you'll see both, in order, for any run:
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 8, marginTop: 14 }}>
          1. Every <code>http_get</code> / <code>http_post</code> / <code>http_post_form</code> call logs its own result
        </p>
        <CodeBlock label="Automatic debug log — you don't write this yourself">
{`POST https://yourapi.com/verify/ → saved as "reg_check": {"success":true,"status":"registered","token":"a1b2c3..."}`}
        </CodeBlock>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          This is the raw response, exactly as your API sent it — before any placeholder ever touches it. If a field you expect
          (e.g. <code>{'{{reg_check.token}}'}</code>) isn't resolving downstream, check this line first: is the field actually there,
          spelled the way you think it is, and not nested one level deeper than you assumed?
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 8, marginTop: 14 }}>
          2. Every <code>if</code> / <code>if_else</code> logs which branch it took, and why
        </p>
        <CodeBlock label="Automatic debug log — condition evaluation">
{`Condition: "{{reg_check.status}}" (value: "registered") equals "registered" (value: "registered") → TRUE → took the THEN branch`}
        </CodeBlock>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          Both the raw placeholder <em>and</em> its resolved value are shown side by side. This is usually the fastest way to catch a
          placeholder that resolved to blank — you'll see something like <code>"{'{{reg_check.data.token}}'}"</code> (value: "") right there in
          the log line, instead of having to guess why a branch went the "wrong" way.
        </p>
        <InfoBox type="tip">
          Set the Logs tab's level filter to <strong>"All levels"</strong> (not just Info/Error) to see these — they're logged at
          <code> debug</code> level. Logs show newest first, so for a multi-step run, read the list from the bottom up to follow it in
          the order it actually happened.
        </InfoBox>

        {/* ── Storage System ── */}
        <SectionHeading id="storage" icon="🗄️" title="Bot Storage System" subtitle="How your bot saves and retrieves data across conversations — sessions, login tokens, user info, and more." />

        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.8, marginBottom: 20 }}>
          Every value you save with <code>set_storage</code> is written to the database and tied to that specific user's WhatsApp number. It persists <strong>indefinitely</strong> across all future messages — until you explicitly delete it. This is how your bot remembers login state, user tokens, preferences, and anything else that needs to survive between conversations.
        </p>

        <p style={{ fontSize: 'clamp(13px, 4vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10 }}>
          Where data lives
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 12 }}>
          Storage is keyed by three things — bot ID, the user's WhatsApp number (<code>chat_id</code>), and the key name you define. Two different users on the same bot have completely separate storage scopes.
        </p>
        <Table
          headers={['Column', 'What it stores']}
          rows={[
            ['Bot ID', 'Which bot this entry belongs to'],
            ['Chat ID', "The user's WhatsApp number — e.g. 2348012345678"],
            ['Key', 'The name you defined — e.g. loginTrue, userApiKey'],
            ['Value', 'Always stored as plain text — numbers, booleans, objects all become strings'],
            ['Updated At', 'Timestamp of the last write — storage has no auto-expiry'],
          ]}
        />

        <InfoBox type="warning">
          <strong>Values are always strings.</strong> If you save <code>{'{{result}}'}</code> (a full JSON object), it gets stored as a JSON string and you <strong>cannot</strong> dot-access sub-fields from it later. Always save individual fields: <code>{'{{result.id}}'}</code>, <code>{'{{result.token}}'}</code>, etc.
        </InfoBox>

        <p style={{ fontSize: 'clamp(13px, 4vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10, marginTop: 24 }}>
          Storage actions
        </p>
        <Table
          headers={['Action', 'What it does']}
          rows={[
            ['set_storage', 'Saves a value permanently for this user. Overwrites if the key already exists.'],
            ['delete_storage', 'Removes a single key for this user.'],
            ['{{storage.key}}', 'Reads a stored value anywhere in your actions — loaded automatically on first access.'],
          ]}
        />

        <p style={{ fontSize: 'clamp(13px, 4vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10, marginTop: 24 }}>
          Login session pattern — the standard way to store tokens
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          After a successful login API call, immediately save everything you'll need later using <code>set_storage</code>:
        </p>
        <CodeBlock label="After successful login — save the session">
{`[
  { "type": "set_storage", "key": "loginTrue",  "value": "yes" },
  { "type": "set_storage", "key": "loginTime",  "value": "{{timestamp}}" },
  { "type": "set_storage", "key": "userApiKey", "value": "{{result.apikey}}" },
  { "type": "set_storage", "key": "userId",     "value": "{{result.id}}" },
  { "type": "set_storage", "key": "userPhone",  "value": "{{chat_id}}" }
]`}
        </CodeBlock>

        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10, marginTop: 16 }}>
          On every command that requires authentication, check the session first:
        </p>
        <CodeBlock label="Auth gate — put this at the top of any protected command">
{`{
  "type": "if",
  "condition": "{{storage.loginTrue}} != yes",
  "then": [{ "type": "jump", "trigger": "/login" }],
  "else": [
    // ... rest of your command
  ]
}`}
        </CodeBlock>

        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10, marginTop: 16 }}>
          Use the saved token in subsequent API calls — no need to log in again:
        </p>
        <CodeBlock label="Using the stored token in an API call">
{`{
  "type": "http_post",
  "url": "https://yourapi.com/transfer",
  "body": {
    "apikey": "{{storage.userApiKey}}",
    "user_id": "{{storage.userId}}",
    "amount": "{{flow.amount}}"
  },
  "save_as": "result"
}`}
        </CodeBlock>

        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10, marginTop: 16 }}>
          On logout, delete every key you set so the session is fully cleared:
        </p>
        <CodeBlock label="Logout — clear the session">
{`[
  { "type": "delete_storage", "key": "loginTrue" },
  { "type": "delete_storage", "key": "loginTime" },
  { "type": "delete_storage", "key": "userApiKey" },
  { "type": "delete_storage", "key": "userId" },
  { "type": "delete_storage", "key": "userPhone" }
]`}
        </CodeBlock>

        <p style={{ fontSize: 'clamp(13px, 4vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10, marginTop: 28 }}>
          All template namespaces — and the critical difference
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 14 }}>
          Not all <code>{'{{...}}'}</code> placeholders work the same way. The most important distinction:
        </p>
        <Table
          headers={['Placeholder', 'Scope', 'Persists?']}
          rows={[
            ['{{storage.key}}', 'Database — per bot + per user', '✅ Forever (until deleted)'],
            ['{{result.field}}', 'Memory — from the last http_post / http_get', '❌ Dies when this message finishes'],
            ['{{flow.field}}', 'Memory — from route_flow (save_to: vars)', '❌ Dies when this message finishes'],
            ['{{conv.step}}', 'Memory — set by conversation_wait', '❌ Only available when the step resumes'],
            ['{{panel.key}}', 'Database — per bot, same for all users', '✅ Until you change it in Panel settings'],
            ['{{chat_id}}', 'Built-in — always available', "✅ The user's WhatsApp number"],
            ['{{name}}', "Built-in — always available", "✅ User's WhatsApp display name"],
            ['{{message}}', 'Built-in — always available', '✅ The raw text they sent'],
            ['{{timestamp}}', 'Built-in — always available', '✅ Unix timestamp of this message'],
          ]}
        />

        <InfoBox type="warning">
          <strong>The most common mistake:</strong> saving data with <code>set_storage</code> in one command, then reading it as <code>{'{{result.field}}'}</code> in another command and getting nothing — because <code>result.*</code> only exists for the duration of the <em>same message</em>. Always use <code>{'{{storage.key}}'}</code> to read anything you saved in a previous interaction.
        </InfoBox>

        <InfoBox type="tip">
          You can view and manually edit all storage entries for any user from your bot's <strong>Storage</strong> tab in the dashboard — useful for debugging or resetting a stuck session.
        </InfoBox>

        {/* ── Broadcasts ── */}
        <SectionHeading id="broadcasts" icon="📣" title="Broadcasts" subtitle="Send one message to many users at once — announcements, promos, and re-engagement." />

        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.8, marginBottom: 20 }}>
          Go to your bot → <strong>📣 Broadcasts</strong> tab → <strong>+ New Broadcast</strong> to message a group of your bot's users in one go — a promo, a service update, a re-engagement nudge to users who've gone quiet.
        </p>

        <InfoBox type="warning">
          Broadcasts are subject to the same <strong>24-hour window</strong> rule as everything else. If most of your recipients last messaged you more than 24 hours ago, your broadcast <strong>must</strong> use a <code>send_template</code> message — free-form text will simply fail to deliver to them. See the <strong>Commands → Templates &amp; the 24-hour window</strong> section above.
        </InfoBox>

        <Step number={1} title="Choose your audience">
          Select recipients from your <strong>Users</strong> list — all users, or a filtered subset (e.g. everyone who messaged in the last 30 days). Double-check the count before sending; there's no way to recall a broadcast once it starts going out.
        </Step>
        <Step number={2} title="Compose the message">
          Use a plain text message (only reliable within the 24-hour window for each recipient) or select an approved template — the same templates you use with <code>send_template</code> in commands.
        </Step>
        <Step number={3} title="Send or schedule">
          Send immediately, or set a future send time — this hands off to the same scheduling engine as <strong>Scheduled Messages</strong> below.
        </Step>
        <Step number={4} title="Track delivery">
          The broadcast's detail view shows delivery status per recipient (sent / delivered / read / failed) so you can see which numbers bounced — usually because they're outside the 24-hour window and the message wasn't a template.
        </Step>

        <InfoBox type="tip">
          Keep broadcast lists reasonably sized and message quality high. Sending too many low-engagement broadcasts can affect your number's WhatsApp quality rating, which in turn affects your messaging limits.
        </InfoBox>

        {/* ── Scheduled Messages ── */}
        <SectionHeading id="scheduled" icon="⏰" title="Scheduled Messages" subtitle="Queue a message to send at a specific future time, per user or per command." />

        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.8, marginBottom: 20 }}>
          Go to your bot → <strong>⏰ Scheduled</strong> tab to queue a message for later — a reminder, a follow-up, a delayed onboarding tip. Unlike Broadcasts, a scheduled message targets a single <code>chat_id</code> (though you can queue many at once), and is often set up programmatically rather than by hand.
        </p>

        <p style={{ fontSize: 'clamp(13px, 4vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10 }}>
          Two ways to schedule
        </p>
        <Table
          headers={['Method', 'Use for']}
          rows={[
            ['Dashboard — Scheduled tab', 'One-off, manually planned messages — e.g. "remind this customer tomorrow at 9am"'],
            ['From a command, via http_post to the Scheduled API', 'Dynamic scheduling driven by bot logic — e.g. "3 days after checkout, ask for a review"'],
          ]}
        />

        <InfoBox type="warning">
          Same rule again: a scheduled message that fires more than 24 hours after the recipient's last message to you needs to be a <code>send_template</code>, or it will fail silently when it's due to send. Check the send log in the Scheduled tab if a queued message shows as failed.
        </InfoBox>

        <InfoBox type="tip">
          Combine <code>set_storage</code> with a scheduled follow-up: save a timestamp when something happens (e.g. an <code>orderTime</code> key set to <code>{'{{timestamp}}'}</code> right after checkout), then use that to decide, in a later scheduled run, whether enough time has passed to send a nudge.
        </InfoBox>

        {/* ── 10. Import & Export ── */}
        <SectionHeading id="import-export" icon="📦" title="Import & Export" subtitle="Back up your bot or share it across accounts." />

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10 
        }}>Export</p>
        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 16 
        }}>
          Bot Overview → <strong>⋯</strong> menu → <strong>Export</strong>. Downloads a <code>.zip</code> containing all your command files and panel field definitions.
        </p>

        <InfoBox type="warning">
          API credentials (token, phone number ID, WABA ID) are <strong>not included</strong> in exports for security. You'll need to re-enter them after importing.
        </InfoBox>

        <p style={{ 
          fontSize: 'clamp(13px, 4vw, 14px)', 
          fontWeight: 700, 
          color: 'var(--text-dark)', 
          marginBottom: 10 
        }}>Import</p>
        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 10 
        }}>
          Bot Overview → <strong>⋯</strong> → <strong>Import</strong>. Choose how to import:
        </p>
        <Table
          headers={['Mode', 'What happens']}
          rows={[
            ['Import into this bot', 'Merges all commands from the package into the current bot. Existing commands with the same trigger are updated.'],
            ['Create new bot', 'Creates a brand-new bot from the package. You still need to add your credentials afterwards.'],
          ]}
        />

        {/* ── 11. Agent Inbox ── */}
        <SectionHeading id="inbox" icon="💬" title="Agent Inbox" subtitle="Let real humans take over WhatsApp conversations from your bot — with full message history, media support, and auto-timeout." />

        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.8, marginBottom: 20 }}>
          The Agent Inbox lets you or your support team take over a WhatsApp conversation from the bot at any point. Once a handoff is triggered, the bot goes completely silent — every message the customer sends goes straight to the inbox where an agent reads and replies manually. The customer sees no difference — it all comes from the same WhatsApp number.
        </p>

        <div style={cardGridStyle}>
          {[
            { icon: '🔄', title: 'Seamless Handoff', desc: 'Bot hands off to a waiting agent queue with one http_post action in your command JSON. Customer gets a connecting message automatically.' },
            { icon: '🖼️', title: 'Full Media Support', desc: 'Images, videos, voice notes, documents, and locations all appear inline in the inbox — agents see everything the customer sends.' },
            { icon: '⏱️', title: 'Auto-Timeout', desc: 'If no agent picks up within 10 minutes, or an agent goes quiet for 5 minutes, the bot takes over again automatically.' },
            { icon: '👥', title: 'Multiple Agents', desc: 'Add sub-agents under your account. They get email alerts for new conversations and can be assigned specific chats.' },
          ].map(f => <Card key={f.title} icon={f.icon} title={f.title}>{f.desc}</Card>)}
        </div>

        <p style={{ fontSize: 'clamp(14px, 4vw, 15px)', fontWeight: 800, color: 'var(--text-dark)', marginBottom: 10, marginTop: 8 }}>
          How the conversation modes work
        </p>
        <Table
          headers={['Mode', 'What it means', 'Who handles messages']}
          rows={[
            ['Bot', 'Normal operation — commands match and respond automatically', 'Bot (automated)'],
            ['Waiting', 'Handoff triggered — customer is on hold waiting for a human', 'Nobody yet'],
            ['Agent', 'A human is actively handling this conversation from the inbox', 'You or your agent'],
            ['Resolved', 'Conversation closed. If customer messages again, bot takes over fresh', 'Bot (fresh session)'],
          ]}
        />

        <InfoBox type="info">
          The platform handles all inbox infrastructure automatically — message storage, media downloads, auto-timeout, and email notifications. You only need to do two things: <strong>trigger a handoff in your command</strong> and <strong>manage conversations from the Inbox tab</strong>.
        </InfoBox>

        <p style={{ fontSize: 'clamp(14px, 4vw, 15px)', fontWeight: 800, color: 'var(--text-dark)', marginBottom: 14, marginTop: 28 }}>
          Step 1 — Add agents (optional)
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 14 }}>
          You can manage the inbox alone as the bot owner — you have full access to all conversations. To add support agents, go to your bot → <strong>💬 Inbox</strong> tab → <strong>Agents</strong> tab → <strong>+ Add Agent</strong>:
        </p>
        <Table
          headers={['Field', 'Description']}
          rows={[
            ['Name', "Agent's display name — shown on their message bubbles in the inbox chat view"],
            ['Email', 'They receive email notifications whenever a new conversation enters the Waiting queue — with a direct link to the inbox'],
            ['Password', 'Login credentials — you set and manage this for them'],
          ]}
        />
        <InfoBox type="tip">
          Agents are optional. If you are the only person managing support, skip this step and handle all conversations yourself from the Inbox tab.
        </InfoBox>

        <p style={{ fontSize: 'clamp(14px, 4vw, 15px)', fontWeight: 800, color: 'var(--text-dark)', marginBottom: 14, marginTop: 28 }}>
          Step 2 — Trigger a handoff from your command
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 14 }}>
          When a customer needs human support, add an <code>http_post</code> action to any command — typically your <code>/support</code> command. You need two values from your bot's <strong>Overview tab</strong>:
        </p>
        <Table
          headers={['Value', 'Where to find it']}
          rows={[
            ['Bot ID', 'Overview tab → Bot Details → Bot ID → click Copy'],
            ['Send Key', 'Overview tab → API Credentials → Send API Key → click Show → Copy'],
          ]}
        />
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          Create a command with trigger <code>/support</code>, Match Type <strong>Exact</strong>, Language <strong>JSON</strong>. Paste this and replace the two placeholder values:
        </p>
        <CodeBlock label="/support command — replace YOUR_BOT_ID and YOUR_SEND_KEY">
{`[
  {
    "type": "http_post",
    "url": "https://www.cmdkit.im/api/bots/inbox/handoff.php",
    "body": {
      "bId": "YOUR_BOT_ID",
      "chatId": "{{chat_id}}",
      "send_key": "YOUR_SEND_KEY"
    },
    "save_as": "handoff_resp"
  },
  {
    "type": "if",
    "condition": "{{result.status}} == ok",
    "then": [
      {
        "type": "send_buttons",
        "body": "🎧 *Support Connected*\n\nYou have been connected to a live agent. Please describe your issue and an agent will respond shortly.",
        "buttons": [
          { "id": "Menu", "title": "← Main Menu" }
        ]
      }
    ],
    "else": [
      {
        "type": "send_buttons",
        "body": "⚠️ We could not connect you to support right now. Please try again shortly.",
        "buttons": [
          { "id": "Menu",     "title": "← Main Menu" },
          { "id": "/support", "title": "Try Again" }
        ]
      }
    ]
  }
]`}
        </CodeBlock>
        <InfoBox type="tip">
          After pasting, click <strong>Save changes</strong> then <strong>Publish</strong>. The command must be Live before customers can trigger it.
        </InfoBox>
        <InfoBox type="warning">
          The <code>url</code> above is fixed — do not change it. It points to the CommandKit handoff endpoint. Only replace <code>YOUR_BOT_ID</code> and <code>YOUR_SEND_KEY</code> with your actual values from the Overview tab.
        </InfoBox>

        <p style={{ fontSize: 'clamp(14px, 4vw, 15px)', fontWeight: 800, color: 'var(--text-dark)', marginBottom: 14, marginTop: 28 }}>
          How to wire the support command into your menu
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 14 }}>
          In whichever command shows your main menu, add a button or list row with <code>/support</code> as the ID. When the customer taps it, the handoff fires automatically:
        </p>
        <CodeBlock label="Add to any send_buttons or send_list action">
{`{ "id": "/support", "title": "Talk to Agent" }`}
        </CodeBlock>
        <InfoBox type="success">
          Once the customer taps the button, the handoff fires, they enter the waiting queue, and you see them in your <strong>💬 Inbox</strong> tab immediately.
        </InfoBox>

        <p style={{ fontSize: 'clamp(14px, 4vw, 15px)', fontWeight: 800, color: 'var(--text-dark)', marginBottom: 14, marginTop: 28 }}>
          Step 3 — Managing conversations from the inbox
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 14 }}>
          Go to your bot → <strong>💬 Inbox</strong> tab. The left sidebar shows all conversations with colour-coded mode badges. Click any conversation to open the full chat history on the right.
        </p>
        <Table
          headers={['Action', 'Where', 'What it does']}
          rows={[
            ['Assign agent', 'Chat header dropdown', 'Assigns an agent to this conversation. Moves from Waiting to Agent mode.'],
            ['Reply', 'Text box at bottom', 'Sends a WhatsApp message from the bot number to the customer. Press Enter or click Send.'],
            ['Hand to Bot', 'Chat header button', 'Returns conversation to bot mode immediately. Customer notified.'],
            ['Clear', 'Chat header button', 'Deletes chat history from your inbox view only. Customer WhatsApp is not affected.'],
            ['Resolve', 'Chat header button', 'Marks conversation resolved. Customer notified. If they message again, bot handles fresh.'],
            ['Reopen', 'Chat header (resolved only)', 'Reopens a resolved conversation back into bot mode.'],
          ]}
        />

        <p style={{ fontSize: 'clamp(14px, 4vw, 15px)', fontWeight: 800, color: 'var(--text-dark)', marginBottom: 14, marginTop: 28 }}>
          Quoting a message (reply-to) and reacting with emoji
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.8, marginBottom: 14 }}>
          Just like the regular WhatsApp app, you can quote a specific message so it's clear which message you're
          responding to, or react to a message with an emoji instead of typing a reply. How you trigger this depends
          on whether you're on a computer or a phone/tablet:
        </p>

        <div style={cardGridStyle}>
          <Card icon="🖱️" title="On a computer (web)">
            <strong>Right-click</strong> any message in the conversation. A small menu pops up with two options:
            <br /><br />
            • <strong>↩ Reply</strong> — quotes that message and puts your cursor in the text box, ready to type
            <br />
            • <strong>🙂 React</strong> — opens a grid of emojis; click one to react
          </Card>
          <Card icon="📱" title="On a phone or tablet (touch)">
            • <strong>Swipe the message to the right</strong> with your finger — a reply icon appears as you drag; let
            go once it's revealed and you're quoting that message
            <br /><br />
            • <strong>Press and hold</strong> a message (about half a second) — the emoji picker pops up straight away
          </Card>
        </div>

        <InfoBox type="tip">
          New to swipe gestures? It's the exact same motion as WhatsApp's own mobile app — swipe right to reply, hold
          down to react. If you've used WhatsApp on your phone before, you already know how this works.
        </InfoBox>

        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 10 }}>
          Once you've quoted a message, you'll see a preview strip above the text box showing what you're replying
          to — click the ✕ on it if you want to cancel and send a normal message instead. When your reply sends, the
          customer sees it exactly like a native WhatsApp quoted reply, tappable back to the original message.
        </p>
        <InfoBox type="info">
          Reply and react only work on messages that have a WhatsApp message ID on record — this covers effectively
          every message going forward. A message sent before this feature existed on your account may not support it.
        </InfoBox>

        <p style={{ fontSize: 'clamp(14px, 4vw, 15px)', fontWeight: 800, color: 'var(--text-dark)', marginBottom: 14, marginTop: 28 }}>
          Filtering conversations
        </p>
        <Table
          headers={['Filter', 'Options', 'Use']}
          rows={[
            ['Mode', 'All / Waiting / Agent / Bot', 'Find conversations by who is currently handling them'],
            ['Status', 'Open / Resolved / All', 'Switch between active and closed conversations'],
          ]}
        />

        <p style={{ fontSize: 'clamp(14px, 4vw, 15px)', fontWeight: 800, color: 'var(--text-dark)', marginBottom: 14, marginTop: 28 }}>
          Media in the inbox
        </p>
        <Table
          headers={['Media type', 'How it appears in inbox']}
          rows={[
            ['Image', 'Rendered inline — click to view full size'],
            ['Video', 'Inline video player'],
            ['Audio / Voice note', 'Inline audio player'],
            ['Document / PDF', 'Download link with filename'],
            ['Location', 'Clickable link that opens Google Maps'],
            ['Sticker', 'Rendered as a small image'],
          ]}
        />
        <InfoBox type="info">
          Media files are downloaded from WhatsApp and stored securely by the platform. They load instantly when you open a conversation — no extra setup required.
        </InfoBox>

        <p style={{ fontSize: 'clamp(14px, 4vw, 15px)', fontWeight: 800, color: 'var(--text-dark)', marginBottom: 14, marginTop: 28 }}>
          Auto-timeout
        </p>
        <Table
          headers={['Scenario', 'Timeout', 'What customer receives']}
          rows={[
            ['No agent picks up', '10 minutes after handoff', 'Buttons: Main Menu and Try Again — with a message that agents are busy'],
            ['Agent goes silent', '5 minutes after last agent reply', 'Buttons: Main Menu and Contact Support — with a message that agent is unavailable'],
          ]}
        />
        <InfoBox type="info">
          Auto-timeout runs automatically in the background — nothing to configure. Customers are never left stuck in a waiting queue indefinitely.
        </InfoBox>

        <p style={{ fontSize: 'clamp(14px, 4vw, 15px)', fontWeight: 800, color: 'var(--text-dark)', marginBottom: 14, marginTop: 28 }}>
          Complete conversation lifecycle
        </p>
        <CodeBlock label="Full flow from message to resolution">
{`1. Customer messages your bot
   → Bot handles automatically (commands, wildcard, etc.)

2. Customer taps "Talk to Agent" (triggers /support command)
   → http_post fires to handoff endpoint
   → Customer receives: "Connecting you to a live agent..."
   → Conversation appears in Inbox tab under WAITING
   → Agents receive an email notification with a direct link

3. You open the Inbox tab
   → See full chat history including any images and media
   → Assign to an agent using the dropdown
   → Conversation moves from WAITING → AGENT mode
   → Bot is completely silent — all customer messages go to inbox only

4. Agent replies from the inbox
   → Message sent to customer via WhatsApp instantly
   → Appears as a dark blue bubble with the agent name label

5. Conversation ends — three ways:
   a) Click Resolve → customer notified, bot handles fresh if they message again
   b) Click Hand to Bot → bot takes over immediately
   c) Auto-timeout → platform returns customer to bot automatically with buttons`}
        </CodeBlock>

        {/* ── 12. Send API ── */}
        <SectionHeading id="send-api" icon="📨" title="Send API" subtitle="Send messages programmatically from your own systems." />

        <p style={{ 
          fontSize: 'clamp(13px, 3.8vw, 14px)', 
          color: 'var(--text-mid)', 
          lineHeight: 1.7, 
          marginBottom: 16 
        }}>
          Every bot has a <strong>Send Key</strong> visible in the Overview tab, under API Credentials. Use it to
          send WhatsApp messages from any external system — your own website, a script, another app — without going
          through the bot's command flow at all. Think of it as a direct "send this message" button you can trigger
          from your own code.
        </p>

        <InfoBox type="warning">
          Keep your Send Key <strong>secret</strong>. Never put it in frontend/browser code where anyone can view
          the page source and see it, and never commit it to a public GitHub repo. Treat it exactly like a password
          — anyone who has it can send WhatsApp messages as your bot.
        </InfoBox>

        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10 }}>
          How authentication works
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 14 }}>
          You can pass your Send Key <strong>either</strong> way — pick whichever is easier for your setup:
        </p>
        <Table
          headers={['Method', 'How']}
          rows={[
            ['HTTP header (recommended)', <><code>X-Send-Key: sk_your_key_here</code></>],
            ['In the request body', <><code>{'{ "send_key": "sk_your_key_here", ... }'}</code></>],
          ]}
        />
        <InfoBox type="info">
          You do <strong>not</strong> need to send a Bot ID anywhere in this request. The Send Key itself already
          identifies which bot is sending the message — one less thing to keep track of.
        </InfoBox>

        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 10, marginTop: 20 }}>
          Request body reference — pick a message type
        </p>
        <p style={{ fontSize: 'clamp(13px, 3.8vw, 14px)', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 14 }}>
          Every request needs <code>to</code> (recipient's number, digits only, including country code — no{' '}
          <code>+</code> or spaces) and <code>type</code>. What else goes in the body depends entirely on{' '}
          <code>type</code> — the tabs below show the <strong>exact, complete JSON body</strong> for each one, not
          just the field names. Click a tab, copy the body, swap in your own values.
        </p>

        <SendApiExplorer />

        <InfoBox type="tip">
          <strong>Copying this into an AI coding assistant?</strong> Paste one full tab's body from above along with
          your Send Key and the recipient's number, and ask it to "write a function that sends a WhatsApp message
          using this API" — everything it needs (the URL, the header name, the field names, and a working example)
          is right there in one place.
        </InfoBox>


        {/* ── 13. Common Errors ── */}
        <SectionHeading id="errors" icon="🛠️" title="Common Errors & Fixes" subtitle="Solutions to the most frequently encountered issues." />

        {[
          {
            title: '"Bot has no WABA ID set"',
            fix: 'Go to Overview → Edit Bot Settings and add your WhatsApp Business Account ID. You get it from Meta Business Manager → Accounts → WhatsApp Accounts.',
          },
          {
            title: 'Webhook messages not arriving',
            fix: 'Check that your webhook URL includes the correct bId, that Meta has verified the webhook, and that you\'ve subscribed to the "messages" field in the webhook settings.',
          },
          {
            title: 'Flow JSON Errors badge shows errors',
            fix: 'Open the Flow JSON Errors tab — it lists every error with the exact path. Fix each one in the Editor tab. All errors must reach 0 before you can publish.',
          },
          {
            title: 'Health check failing — HTTP 410 or 404',
            fix: 'Your endpoint URL is wrong or the server isn\'t running. Verify the path is correct and publicly accessible (not localhost). Check your server logs for details.',
          },
          {
            title: 'Health check failing — "Decryption failed"',
            fix: 'Your endpoint is not correctly decrypting Meta\'s AES payload. Ensure your private key matches the public key uploaded to Meta. Re-generate and re-upload the key pair if needed.',
          },
          {
            title: 'Health check failing — SSL error',
            fix: 'Your endpoint must use HTTPS with a valid, publicly trusted certificate. Self-signed certificates are rejected by Meta. Renew or replace your certificate.',
          },
          {
            title: '"Sign public key" step not turning green',
            fix: 'Click the step → if the API can\'t confirm automatically (some WABA configurations don\'t expose this field), click "Key is already uploaded — mark as done" to override manually.',
          },
          {
            title: 'Access token expired',
            fix: 'The temporary Meta token lasts 24 hours. Generate a permanent token via a System User in Meta Business Manager and update it in your bot settings.',
          },
          {
            title: 'WhatsApp Health shows "Messaging Tier: —"',
            fix: 'This usually resolves itself on the next click of Refresh. Meta occasionally moves which field this number comes from; if it stays blank for more than a day, your token may be missing a permission — check that your Meta app has the whatsapp_business_management permission.',
          },
          {
            title: 'Business Profile photo upload fails',
            fix: 'This needs your bot\'s Meta App ID filled in (different from your WhatsApp token or WABA ID). Go to Overview → Edit Bot Settings → Meta App ID, and get the value from Meta for Developers → your app → Settings → Basic → "App ID". Also check the photo is a JPEG or PNG under 5MB.',
          },
          {
            title: 'Right-click reply/react menu doesn\'t appear',
            fix: 'Make sure you\'re right-clicking directly on a message bubble, not the empty space around it. This only works on messages sent after the feature was added — very old messages may not have the WhatsApp message ID needed to reply/react to them.',
          },
          {
            title: 'A placeholder like {{save_as.field}} always resolves empty',
            fix: 'Check the automatic debug log for that http_post/http_get call (see Users & Logs) — it shows the raw response exactly as your API sent it. The field name is usually either misspelled or nested one level deeper than expected (e.g. it\'s actually {{save_as.data.field}}, not {{save_as.field}}). Nesting works at any depth, so once you find the real path, just use it as written.',
          },
          {
            title: '"for_each: items did not resolve to an array, skipping"',
            fix: 'Make sure "items" is exactly one {{placeholder}} with nothing else in the string — not mixed with other text. If it still fails, check the automatic debug log for the HTTP call that produced it: confirm the field is genuinely an array in the raw response, and that the path (including any nesting) is spelled exactly right.',
          },
          {
            title: 'An Authorization header with {{storage.x}} isn\'t reaching the API',
            fix: 'Header values fully support placeholders in http_get, http_post, and http_post_form alike. Confirm the value you\'re reading (e.g. {{storage.userToken}}) was actually saved earlier in this user\'s session — check the Storage tab for that user\'s chat ID, or add a debug log action right before the call to print what it currently resolves to.',
          },
        ].map(({ title, fix }) => (
          <div key={title} style={{
            background: 'var(--bg-card)', 
            border: '1px solid var(--border)',
            borderRadius: 'clamp(8px, 3vw, 10px)', 
            padding: 'clamp(14px, 4vw, 16px) clamp(16px, 5vw, 20px)', 
            marginBottom: 12,
          }}>
            <div style={{ 
              fontWeight: 700, 
              fontSize: 'clamp(13px, 4vw, 14px)', 
              color: 'var(--text-dark)', 
              marginBottom: 6, 
              fontFamily: 'monospace',
              wordBreak: 'break-word',
            }}>
              {title}
            </div>
            <div style={{ 
              fontSize: 'clamp(12px, 3.5vw, 13.5px)', 
              color: 'var(--text-mid)', 
              lineHeight: 1.7 
            }}>{fix}</div>
          </div>
        ))}

        <div style={{ 
          marginTop: 'clamp(32px, 8vw, 48px)', 
          padding: 'clamp(20px, 5vw, 24px)', 
          background: 'var(--primary-light)', 
          borderRadius: 'clamp(12px, 4vw, 14px)', 
          textAlign: 'center' 
        }}>
          <div style={{ fontSize: 'clamp(20px, 6vw, 22px)', marginBottom: 8 }}>🚀</div>
          <div style={{ 
            fontWeight: 800, 
            fontSize: 'clamp(14px, 4.5vw, 16px)', 
            color: 'var(--primary)', 
            marginBottom: 6 
          }}>You're ready to build.</div>
          <div style={{ 
            fontSize: 'clamp(12px, 3.5vw, 13.5px)', 
            color: 'var(--text-mid)' 
          }}>
            Create your first bot, write a command, and send a message on WhatsApp in minutes.
          </div>
        </div>

        <div style={{ height: isMobile ? 40 : 60 }} />
      </main>

      {/* Mobile Navigation Bottom Sheet */}
      <MobileNav
        isOpen={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
        activeSection={activeSection}
        onSelect={scrollTo}
        collapsed={collapsedCategories}
        onToggleCategory={toggleCategory}
      />

      {/* Always-visible floating nav trigger (mobile). `position: fixed`
          so it stays put regardless of scroll — see the note above
          asideSpacerRef for why this project avoids relying on `sticky`
          for load-bearing navigation. */}
      {isMobile && !isMobileNavOpen && (
        <button
          onClick={() => setIsMobileNavOpen(true)}
          style={{
            position: 'fixed',
            bottom: 24,
            right: 20,
            zIndex: 150,
            background: 'var(--primary)',
            border: 'none',
            borderRadius: 30,
            padding: '13px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            color: '#fff',
            fontWeight: 700,
            fontSize: 14,
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          }}
        >
          <span>📖</span> Menu
        </button>
      )}
    </div>
  );
}